import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from 'react';
import { MAX_TILES } from '../../constants';
import { formatDirection } from '../../utils/shipmentHelpers';
import { isDockUnavailableForMovement } from '../../utils/dock';
import CampusFacilityMultiSelect from 'components/Campus/CampusFacilityMultiSelect';
import DestinationFacilityFilter from 'components/Campus/DestinationFacilityFilter';
import { useModalDestinations } from 'hooks/useModalDestinations';
import { withCampusFacilities } from 'utils/movementVariables';
import { facilityDisplayName } from 'utils/campusLabels';
import { detectAndroidDevice } from '../../utils/deviceDetection';
import { 
  CircularProgress, 
  Modal, 
  Tooltip, 
  Paper, 
  Tab, 
  Tabs, 
  Box, 
  ToggleButton, 
  ToggleButtonGroup, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  IconButton, 
  List, 
  ListItem, 
  ListItemAvatar, 
  ListItemText, 
  Avatar, 
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  TextField,
  Autocomplete,
  Chip,
  InputAdornment
} from '@mui/material';
import { 
  Close, 
  Edit, 
  Check, 
  ArrowForward, 
  History, 
  SwapHoriz, 
  FilterList, 
  Add, 
  Delete,
  LocalParking,
  Refresh,
  Search,
  Clear
} from '@mui/icons-material';
import Select from 'react-select';
import { twMerge } from 'tailwind-merge';
import { useYardData, YardSpot, Status, Zone, HistoryEntry, YardHistory } from './yardDataFetch';
import axios from 'axios';
import TaskDetailsModal from '../../components/TaskDetailsModal/TaskDetailsModal';
import TrailerHistoryTab from './TrailerHistoryTab';

import { getAuthToken } from '../../utils/cookieUtils';
import { lineageCheckIn, type ActiveLineage } from 'utils/lineage';
import LocationVerification from 'components/YardManagement/LocationVerification';
// GraphQL API URL constant (used for direct API calls like dock assignment)
const GRAPHQL_API_URL = process.env.REACT_APP_GRAPHQL_API || 'http://localhost:4000/api/graphql';

// Helper to merge lock sets
const mergeLockSets = (lockSet1: Set<string>, lockSet2: Set<string>): Set<string> => {
  const merged = new Set<string>();
  lockSet1.forEach(id => merged.add(id));
  lockSet2.forEach(id => merged.add(id));
  return merged;
};

// Local type definitions
interface StagingTrailer {
  puid: string;
  driver_id: string;
  driver_name: string;
  driver_phone_number: string;
  truck_number: string;
  trailer_number: string;
  direction: string;
  carrier_name?: string;
  trailer_uuid?: string; // Add trailer UUID field
  current_status?: string; // Add current_status for loaded trailer detection
  shipment_number?: string; // Shipment reference number field
}

// Interface for zone-based trailers
interface ZoneTrailer {
  puid: string;
  driver_id: string;
  driver_name: string;
  driver_phone_number?: string;
  truck_number?: string;
  trailer_number: string;
  direction: string;
  carrier_name?: string;
  zone_id: string;
  current_status?: string;
  trailer_uuid?: string; // Add trailer UUID field
  shipment_number?: string; // Shipment reference number field
  shipment_id?: string; // Shipment ID field (for preserving shipment relationship)
  active_lineage?: ActiveLineage;
  data?: any; // Add data field for trailer data including OOS status
  [key: string]: any;
}

// Add interface for the payload
interface AssignYardSpotPayload {
  yard_spot: string;
  carrier_name?: string;
  [key: string]: any; // Allow additional properties
}

// Add interface for assigned yard slots response
interface AssignedYardSlot {
  carrier_name?: string;
  driver_id: string;
  driver_name: string;
  driver_phone_number: string;
  yard_spot: string;
  trailer_number: string;
  puid: string;
  [key: string]: any; // For other properties
}

// Update the imported YardSpot interface with our own that includes carrier_name
// We need to do this since we don't want to modify the import file directly
interface LocalYardSpot extends YardSpot {
  // carrier_name is now included in the YardSpot interface
  direction?: 'inbound' | 'outbound' | null;
}

// Unified interface for modal content (can represent either a yard spot or zone trailer)
interface UnifiedModalItem {
  type: 'yard_spot' | 'zone_trailer';
  id: string;
  displayName: string;
  
  // Trailer info (common to both)
  trailerNumber?: string | null;
  carrierName?: string | null;
  shipmentNumber?: string | null;
  direction?: string | null;
  status?: string | null;
  
  // Driver info (common to both)
  driverId?: string | null;
  driverName?: string | null;
  driverPhone?: string | null;
  
  // Yard spot specific
  spotNumber?: string;
  spotStatus?: Status;
  zoneId?: string;
  notes?: string;
  active_lineage?: ActiveLineage;
  /** The spot's zone, by name. */
  zoneName?: string;
  /** The facility the item is at, by name: set on campus roll-ups. */
  facilityName?: string;
  /** An empty spot's own verification timestamps. */
  lastRtlsEmptyAt?: string | null;
  lastManualCheckAt?: string | null;
  
  // Zone trailer specific
  puid?: string;
  currentZone?: string;
  trailerUuid?: string;
  
  // Original data reference
  originalData: LocalYardSpot | ZoneTrailer;
}

// Interface for existing trailers in autocomplete
interface ExistingTrailer {
  scac: string;
  trailerNumber: string;
  trailerUuid: string;
  location: string;
  shipmentNumber?: string;
  isAtDock?: boolean;
  trailerStatus?: 'EMPTY' | 'LOADED' | string;
}

// Types
type ScheduledTime = {
  startTime: Date;
  endTime: Date;
  driverId: string | null;
  driverName: string | null;
  trailerId: string | null;
  trailerNumber: string | null;
  shipmentNumber: string | null;
};


// Helper functions
const getRandomColor = () => {
  const colors = [
    '#4CAF50', '#2196F3', '#FF9800', '#E91E63', 
    '#9C27B0', '#00BCD4', '#FFEB3B', '#795548'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

// Reusable Tile Component
interface TileProps {
  label: string;
  status: 'available' | 'in-use' | 'inactive' | 'empty' | 'loaded' | 'inbound' | 'outbound' | 'unavailable';
  zoneColor?: string;
  onClick?: () => void;
  tooltip?: React.ReactNode;
  actionButton?: {
    label: string;
    onClick: (e: React.MouseEvent) => void;
    icon?: React.ReactNode;
    className?: string;
  };
  actionButtons?: Array<{
    label: string;
    onClick: (e: React.MouseEvent) => void;
    icon?: React.ReactNode;
    className?: string;
  }>;
  // New props for enhanced display
  spotName?: string; // For yard spots, this will be the spot number (e.g., "Y15")
  trailerNumber?: string; // When there's a trailer in the spot
  carrierName?: string; // SCAC code
  shipmentNumber?: string; // Shipment reference number
  isZoneTrailer?: boolean; // To distinguish zone trailers from yard spots
  isLocked?: boolean; // To show gray overlay when processing
  onTaskClick?: () => void; // Handler for clicking on task overlay
  isTaskBasedLock?: boolean; // To distinguish between task-based and manual locks
  shipmentDirection?: 'inbound' | 'outbound' | null; // Shipment direction for indicator
  // New props for multiple locks support
  locks?: any[]; // Array of lock objects for this tile
  notes?: string; // Notes for unavailable spots
}

const Tile: React.FC<TileProps> = ({ 
  label, 
  status, 
  zoneColor, 
  onClick, 
  tooltip, 
  actionButton, 
  actionButtons,
  spotName,
  trailerNumber,
  carrierName,
  shipmentNumber,
  isZoneTrailer = false,
  isLocked = false,
  onTaskClick,
  isTaskBasedLock = false,
  shipmentDirection = null,
  locks = [],
  notes
}) => {
  // Detect if on Android device for responsive sizing
  const isAndroid = detectAndroidDevice();
  
  // Helper function to get lock display text
  const getLockDisplayText = () => {
    if (!isTaskBasedLock) {
      return 'Processing...';
    }
    
    // Always show "Active Task" for any task-based lock, regardless of count
    return 'Active Task';
  };

  const getStatusColors = () => {
    switch (status) {
      case 'available':
        return { bg: 'bg-green-100', border: 'border-green-400', header: 'bg-green-500' };
      case 'in-use':
        return { bg: 'bg-white', border: 'border-gray-400', header: 'bg-gray-500' };
      case 'inactive':
        return { bg: 'bg-red-100', border: 'border-red-400', header: 'bg-red-500' };
      case 'empty':
        return { bg: 'bg-yellow-200', border: 'border-yellow-400', header: 'bg-yellow-500' };
      case 'loaded':
        return { bg: 'bg-blue-100', border: 'border-blue-400', header: 'bg-blue-500' };
      case 'inbound':
        return { bg: 'bg-yellow-200', border: 'border-green-600', header: 'bg-green-600' };
      case 'outbound':
        return { bg: 'bg-blue-100', border: 'border-blue-400', header: 'bg-blue-500' };
      case 'unavailable':
        return { bg: 'bg-gray-100', border: 'border-gray-400', header: 'bg-gray-500' };
      default:
        return { bg: 'bg-yellow-100', border: 'border-yellow-400', header: 'bg-yellow-500' };
    }
  };

  const colors = getStatusColors();


  
  // Determine which buttons to show
  const buttonsToShow = actionButtons || (actionButton ? [actionButton] : []);
  
  const tileContent = (
    <div
      className={`w-32 h-32 rounded shadow cursor-pointer flex flex-col relative ${colors.bg} ${colors.border} border-2 hover:opacity-90 transition-opacity`}
      onClick={onClick}
      style={{ borderColor: zoneColor }}
    >
      {/* Gray overlay for locked tiles */}
      {isLocked && (
        <div 
          className="absolute inset-0 bg-gray-500 bg-opacity-50 rounded flex items-center justify-center z-20 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (isTaskBasedLock && onTaskClick) {
              onTaskClick();
            }
          }}
        >
          <div className="bg-white px-2 py-1 rounded text-xs font-medium text-gray-700 shadow">
            {getLockDisplayText()}
          </div>
        </div>
      )}
      <div className={`h-4 w-full rounded-t ${colors.header}`}></div>
      
      {/* Spot name in top left corner (for yard spots only) */}
      {!isZoneTrailer && spotName && (
        <div className="absolute top-1 left-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
          {spotName}
        </div>
      )}
      
      {/* Shipment direction indicator in top right corner */}
      {shipmentDirection && (
        <div className="absolute top-1 right-1 bg-black bg-opacity-70 text-white text-xs px-1 py-0.5 rounded text-center font-medium">
          {shipmentDirection === 'inbound' ? 'I' : 'O'}
        </div>
      )}
      
      {/* Main content area */}
      <div className="flex flex-1 flex-col px-2 py-1">
        {trailerNumber ? (
          // Show trailer info when there's a trailer - pinned to top
          <div className="text-center mt-1">
            <div className="font-bold text-sm mb-1 leading-tight">{trailerNumber}</div>
            {carrierName && (
              <div className="text-xs text-gray-700 font-medium leading-tight">{carrierName}</div>
            )}
            {shipmentNumber && (
              <div className="text-xs text-gray-500 leading-tight">{shipmentNumber}</div>
            )}
          </div>
        ) : isZoneTrailer ? (
          // For zone trailers, show the label (trailer number) - pinned to top
          <div className="text-center mt-1">
            <span className="font-bold text-lg leading-tight">{label}</span>
          </div>
        ) : (
          // For empty yard spots, show "Available" or status - centered
          <div className="flex flex-1 items-center justify-center">
            {status === 'unavailable' ? (
              // Show notes for unavailable spots
              <div className="text-center px-1">
                <div className="font-medium text-sm text-red-700 mb-1">Unavailable</div>
                <div className="text-xs text-gray-600 leading-tight break-words">
{notes ? notes : `[No notes - notes value: "${notes}"]`}
                </div>
              </div>
            ) : (
              <span className="font-medium text-sm text-center leading-tight text-gray-600">
                {status === 'available' ? 'Available' : status.charAt(0).toUpperCase() + status.slice(1)}
              </span>
            )}
          </div>
        )}
      </div>
      
      {/* Action Buttons */}
      {buttonsToShow.length > 0 && (
        <div className={`absolute bottom-0.5 left-0.5 right-0.5 flex flex-col`} style={{ gap: isAndroid ? '1px' : '2px' }}>
          {buttonsToShow.map((button, index) => (
            <button
              key={index}
              className={`tile-action-button w-full rounded shadow-sm ${
                button.className || 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
              style={{ 
                fontSize: isAndroid ? '7px' : '0.75rem', 
                lineHeight: '1', 
                padding: isAndroid ? '1px 2px' : '2px 4px',
                height: isAndroid ? '12px' : 'auto',
                maxHeight: isAndroid ? '12px' : undefined,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={(e) => {
                e.stopPropagation(); // Prevent triggering the tile's onClick
                button.onClick(e);
              }}
              title={button.label}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isAndroid ? '1px' : '4px', lineHeight: '1' }}>
                {button.icon && <span style={{ fontSize: isAndroid ? '7px' : '12px', lineHeight: '1', display: 'flex', alignItems: 'center' }}>{button.icon}</span>}
                <span style={{ lineHeight: '1', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{button.label}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return tooltip ? (
    <Tooltip title={tooltip} arrow>
      {tileContent}
    </Tooltip>
  ) : tileContent;
};

// Add this function right after your existing helper functions (around line 100)
// Function to determine if a yard spot contains an empty trailer
const isEmptyTrailer = (spot: LocalYardSpot) => {
  return spot.status === 'empty';
};

// Function to determine if a yard spot contains a loaded trailer
const isLoadedTrailer = (spot: LocalYardSpot) => {
  return spot.status === 'loaded';
};

// Function to determine if a trailer is out of service (OOS)
const isTrailerOOS = (trailer: LocalYardSpot | ZoneTrailer) => {
  return trailer.data?.oos === true;
};

// Utility functions for unified modal
const createUnifiedItemFromSpot = (spot: LocalYardSpot, zones: Zone[]): UnifiedModalItem => {
  const zone = zones.find(z => z.id === spot.zoneId);
  return {
    type: 'yard_spot',
    id: spot.id,
    displayName: `Spot ${spot.displayNumber}`,
    
    // Trailer info
    trailerNumber: spot.trailerNumber,
    carrierName: spot.carrier_name,
    shipmentNumber: spot.shipmentNumber,
    direction: spot.direction,
    status: spot.trailerStatus,
    
    // Driver info
    driverId: spot.driverId,
    driverName: spot.driverName,
    
    // Yard spot specific
    spotNumber: spot.displayNumber,
    spotStatus: spot.status,
    zoneId: spot.zoneId,
    notes: spot.notes,
    active_lineage: spot.active_lineage,
    zoneName: zone?.baseName || zone?.name,
    facilityName: spot.facilityName,
    lastRtlsEmptyAt: spot.lastRtlsEmptyAt,
    lastManualCheckAt: spot.lastManualCheckAt,
    
    // Original data
    originalData: spot
  };
};

const createUnifiedItemFromZoneTrailer = (trailer: ZoneTrailer, zones: Zone[]): UnifiedModalItem => {
  const zone = zones.find(z => z.id === trailer.zone_id);
  return {
    type: 'zone_trailer',
    id: trailer.puid,
    displayName: `Trailer ${trailer.trailer_number}`,
    
    // Trailer info
    trailerNumber: trailer.trailer_number,
    carrierName: trailer.carrier_name,
    shipmentNumber: trailer.shipment_number,
    direction: trailer.direction,
    status: trailer.current_status,
    
    // Driver info
    driverId: trailer.driver_id,
    driverName: trailer.driver_name,
    driverPhone: trailer.driver_phone_number,
    
    // Zone trailer specific
    puid: trailer.puid,
    currentZone: zone?.name || 'Unknown Zone',
    zoneName: zone?.baseName || zone?.name,
    facilityName: zone?.facilityName,
    trailerUuid: trailer.trailer_uuid,
    active_lineage: trailer.active_lineage,
    
    // Original data
    originalData: trailer
  };
};

// Component
const YardSpotManagement = React.memo(function YardSpotManagement() {
  // *** YARD SPOT ORDERING STRATEGY ***
  // This component uses a two-step discrete approach to ensure yard spot positioning is never affected by trailer assignments:
  // Step 1: Establish yard spot positions based on their original API order (order_number field, then displayNumber for secondary sort)
  // Step 2: Populate those spots with trailer information without affecting the established order
  // This ensures that spots maintain their physical positions regardless of what trailers are assigned to them.
  // Note: Since order_number often defaults to 0, we use displayNumber (e.g., "A1", "A2") as secondary sort for consistent ordering.
  
  // *** PERFORMANCE OPTIMIZATION ***
  // This component implements several optimizations to prevent unnecessary re-renders:
  // 1. Data fetching is debounced to prevent excessive API calls
  // 2. State updates only occur when data actually changes (using JSON string comparison)
  // 3. Manual refresh allows users to explicitly update data when needed
  // 4. The useYardData hook implements memoization and selective updates
  // 5. Callback functions are memoized to prevent recreation on each render
  // 6. Yard spot assignments and removals update state directly without reloading all data
  // These optimizations significantly improve performance and user experience.
  
  // Local UI state declaration first
  // LocalStorage keys for yard management preferences
  const STORAGE_KEYS = {
    ZONE_FILTER: 'yardManagement_zoneFilter',
    SPOT_BATCH: 'yardManagement_spotBatch',
    SHOW_UNALLOCATED: 'yardManagement_showUnallocated'
  };
  
  // Load saved preferences from localStorage
  const loadSavedPreferences = () => {
    try {
      const savedZone = localStorage.getItem(STORAGE_KEYS.ZONE_FILTER);
      const savedBatch = localStorage.getItem(STORAGE_KEYS.SPOT_BATCH);
      const savedShowUnallocated = localStorage.getItem(STORAGE_KEYS.SHOW_UNALLOCATED);
      
      return {
        zoneFilter: savedZone || null,
        spotBatch: savedBatch || null,
        showUnallocated: savedShowUnallocated === null ? true : savedShowUnallocated === 'true' // Default true
      };
    } catch (error) {
      console.error('Error loading saved preferences:', error);
      return { zoneFilter: null, spotBatch: null, showUnallocated: false };
    }
  };
  
  const savedPrefs = loadSavedPreferences();
  
  const [filterZone, setFilterZone] = useState<string | null>(savedPrefs.zoneFilter);
  const [filterStatus, setFilterStatus] = useState<Status | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [spotBatchFilter, setSpotBatchFilter] = useState<string | null>(savedPrefs.spotBatch);
  const [showUnallocatedAssets, setShowUnallocatedAssets] = useState<boolean>(savedPrefs.showUnallocated);

  // Toggle a filter tag in the search term (EMPTY, LOADED, OOS)
  const toggleSearchTag = (tag: string) => {
    const upperTag = tag.toUpperCase();
    const words = searchTerm.split(/\s+/).filter(w => w.length > 0);
    const tagIndex = words.findIndex(w => w.toUpperCase() === upperTag);

    if (tagIndex !== -1) {
      words.splice(tagIndex, 1);
    } else {
      if (upperTag === 'EMPTY') {
        const loadedIdx = words.findIndex(w => w.toUpperCase() === 'LOADED');
        if (loadedIdx !== -1) words.splice(loadedIdx, 1);
      } else if (upperTag === 'LOADED') {
        const emptyIdx = words.findIndex(w => w.toUpperCase() === 'EMPTY');
        if (emptyIdx !== -1) words.splice(emptyIdx, 1);
      }
      words.push(upperTag);
    }
    setSearchTerm(words.join(' '));
  };

  const searchHasTag = (tag: string) => {
    const words = searchTerm.split(/\s+/).filter(w => w.length > 0);
    return words.some(w => w.toUpperCase() === tag.toUpperCase());
  };

  // Unified modal state
  const [showUnifiedDetailsModal, setShowUnifiedDetailsModal] = useState<boolean>(false);
  const [selectedUnifiedItem, setSelectedUnifiedItem] = useState<UnifiedModalItem | null>(null);

  const [showZoneManageModal, setShowZoneManageModal] = useState<boolean>(false);
  const [showZoneEditModal, setShowZoneEditModal] = useState<boolean>(false);
  const [timeRange, setTimeRange] = useState<string>('week');
  const [newZoneName, setNewZoneName] = useState<string>('');
  const [newZoneColor, setNewZoneColor] = useState<string>(getRandomColor());
  const [isEndingAssignment, setIsEndingAssignment] = useState<boolean>(false);
  const [endAssignmentError, setEndAssignmentError] = useState<string | null>(null);
  const [selectedSpot, setSelectedSpot] = useState<LocalYardSpot | null>(null);

  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  
  // Create refs for main content areas to maintain their dimensions
  const stagingAreaRef = useRef<HTMLDivElement>(null);
  const spotsContainerRef = useRef<HTMLDivElement>(null);
  const [stagingHeight, setStagingHeight] = useState<number | null>(null);
  const [spotsHeight, setSpotsHeight] = useState<number | null>(null);
  
  // Use a more robust scroll restoration approach
  const [isRestoringScroll, setIsRestoringScroll] = useState(false);
  const scrollY = useRef(0);
  
  // Fix the useYardData call with proper property names
  const {
    loading: apiLoading,
    yardSpots: rawYardSpots,
    yardHistory: apiYardHistory,
    error,
    facilities,
    selectedFacility,
    setSelectedFacility,
    timeInterval,
    setTimeInterval,
    zones,
    facilityZones,
    refreshData,
    // Warehouse data
    warehouseData,
    // Task-based locks
    taskBasedLockedSpots,
    taskBasedLockedTrailers,
    activeLocks,
    allFacilityLocks,
    getLocksForYardSpot,
    // Campus roll-up: every facility of the selected facility's campus
    campusFacilities,
    selectedCampusFacilityIds,
    setSelectedCampusFacilityIds
  } = useYardData(undefined, 60000, { campusRollup: true }); // Use 60 second refresh interval
  // The stored selection can predate facility_alias, so label it from the fetched list.
  const selectedFacilityLabel = facilityDisplayName(
    facilities.find(f => f.id === selectedFacility?.id) ?? selectedFacility
  );

  // Add state for local modifications to yardSpots, with extended type to include carrier_name
  const [localModifications, setLocalModifications] = useState<{[spotId: string]: Partial<YardSpot> & {carrier_name?: string | null}}>({});

  // Always use max time interval
  useEffect(() => {
    setTimeInterval('8760'); // Always set to max (1 year)
  }, [setTimeInterval]);
  
  // Step 1: Establish yard spot positions based on their original order
  const sortedYardSpots = useMemo(() => {
    // If the API returns empty array, return empty array
    if (rawYardSpots.length === 0) return [];
    

    
    // Preserve the original API ordering - this should already be sorted by order_number
    // Do NOT apply any modifications here that could affect ordering
    return [...rawYardSpots];
  }, [rawYardSpots]);

  // Step 2: Populate spots with trailer information as a separate step
  const yardSpots = useMemo(() => {
    // Apply local modifications on top of the ordered spots
    // This step only adds/modifies trailer data, never changes positioning
    return sortedYardSpots.map(spot => {
      const modifications = localModifications[spot.id];
      
      // Apply modifications if they exist, otherwise just use the original spot
      // Preserve order_number and other positioning fields from the original spot
      return { 
        ...spot, 
        ...(modifications || {}),
        // Explicitly preserve ordering fields to prevent modification
        order_number: spot.order_number, // Always use original order_number
        displayNumber: spot.displayNumber, // Always use original displayNumber
        number: spot.number // Always use original number
      } as LocalYardSpot;
    });
  }, [sortedYardSpots, localModifications]);

  // Function to save zone filter preference
  const saveZoneFilterPreference = useCallback(() => {
    try {
      if (filterZone) {
        localStorage.setItem(STORAGE_KEYS.ZONE_FILTER, filterZone);
      } else {
        localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
      }
    } catch (error) {
      console.error('Error saving zone filter preference:', error);
    }
  }, [filterZone, STORAGE_KEYS.ZONE_FILTER]);

  // Function to clear all filters
  const clearAllFilters = useCallback(() => {
    // Clear state
    setFilterZone(null);
    setSpotBatchFilter(null);
    setShowUnallocatedAssets(false);
    setSearchTerm('');
    
    // Clear localStorage
    try {
      localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
      localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
      localStorage.removeItem(STORAGE_KEYS.SHOW_UNALLOCATED);
    } catch (error) {
      console.error('Error clearing filter preferences:', error);
    }
  }, [STORAGE_KEYS.ZONE_FILTER, STORAGE_KEYS.SPOT_BATCH, STORAGE_KEYS.SHOW_UNALLOCATED]);

  // Function to generate intelligent spot batches based on selected zone
  const generateSpotBatches = useMemo(() => {
    if (!yardSpots || yardSpots.length === 0 || !filterZone) return [];
    
    // Filter spots by selected zone first
    const zoneSpotsFiltered = yardSpots.filter(spot => spot.zoneId === filterZone);
    
    if (zoneSpotsFiltered.length === 0) return [];
    
    // Extract numeric values from spot names/displayNumbers for this zone
    const spotNumbers = zoneSpotsFiltered
      .map(spot => {
        const match = spot.displayNumber.match(/\d+/);
        return match ? parseInt(match[0], 10) : null;
      })
      .filter((num): num is number => num !== null)
      .sort((a, b) => a - b);
    
    if (spotNumbers.length === 0) return [];
    
    // Remove duplicates
    const uniqueNumbers = Array.from(new Set(spotNumbers));
    
    // Generate batches based on decade ranges (1-10, 11-20, 191-200, 201-210, etc.)
    const batches: { label: string; start: number; end: number }[] = [];
    const minNumber = Math.min(...uniqueNumbers);
    const maxNumber = Math.max(...uniqueNumbers);
    
    // Calculate the starting decade range
    let rangeStart = Math.floor((minNumber - 1) / 10) * 10 + 1;
    
    while (rangeStart <= maxNumber) {
      const rangeEnd = rangeStart + 9;
      
      // Find actual spots in this range
      const spotsInRange = uniqueNumbers.filter(num => num >= rangeStart && num <= rangeEnd);
      
      if (spotsInRange.length > 0) {
        // Use the actual first and last spot numbers in this range
        const actualStart = Math.min(...spotsInRange);
        const actualEnd = Math.max(...spotsInRange);
        
        batches.push({
          label: `${actualStart}-${actualEnd}`,
          start: actualStart,
          end: actualEnd
        });
      }
      
      rangeStart += 10;
    }
    
    return batches;
  }, [yardSpots, filterZone]);

  
  // Use derivation functions instead of separate state
  const displayedYardSpots = useMemo(() => {
    if (!yardSpots) return [];
    return yardSpots.filter(spot => {
      if (filterZone && spot.zoneId !== filterZone) return false;
      if (filterStatus && spot.status !== filterStatus) return false;
      
      // Apply spot batch filter
      if (spotBatchFilter) {
        const batch = generateSpotBatches.find(b => b.label === spotBatchFilter);
        if (batch) {
          const match = spot.displayNumber.match(/\d+/);
          const spotNum = match ? parseInt(match[0], 10) : null;
          if (spotNum === null || spotNum < batch.start || spotNum > batch.end) {
            return false;
          }
        }
      }
      
      if (searchTerm.trim()) {
        const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
        const hasOosFilter = searchWords.includes('oos');
        const hasEmptyFilter = searchWords.includes('empty');
        const hasLoadedFilter = searchWords.includes('loaded');
        const remainingWords = searchWords.filter(w => w !== 'oos' && w !== 'empty' && w !== 'loaded');

        // OOS filter: spot must have oos flag
        if (hasOosFilter && !spot.data?.oos) return false;
        // EMPTY filter: spot must have empty status
        if (hasEmptyFilter && spot.status !== 'empty') return false;
        // LOADED filter: spot must have loaded status
        if (hasLoadedFilter && spot.status !== 'loaded') return false;

        if (remainingWords.length > 0) {
          const searchableText = [
            spot.displayNumber,
            spot.driverName || '',
            spot.trailerNumber || '',
            spot.shipmentNumber || '',
            spot.carrier_name || ''
          ].join(' ').toLowerCase();

          if (!remainingWords.every(word => searchableText.includes(word))) return false;
        }
      }
      return true;
    });
  }, [yardSpots, filterZone, filterStatus, searchTerm, spotBatchFilter, generateSpotBatches]);


  
  // Group spots by zone - memoized to prevent recalculation
  // This preserves the established spot ordering from the two-step process above
  const spotsByZone = useMemo(() => {
    if (!yardSpots) return [];
    return zones
      .sort((a, b) => a.name.localeCompare(b.name)) // Sort zones alphabetically
      .map(zone => ({
        zone: {
          ...zone,
          color: zone.color || '#2196F3' // Default to blue if no color is defined
        },
        spots: yardSpots.filter(spot => spot.zoneId === zone.id)
          .filter(spot => {
            if (filterStatus && spot.status !== filterStatus) return false;
            if (searchTerm.trim()) {
              const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
              const hasOosFilter = searchWords.includes('oos');
              const hasEmptyFilter = searchWords.includes('empty');
              const hasLoadedFilter = searchWords.includes('loaded');
              const remainingWords = searchWords.filter(w => w !== 'oos' && w !== 'empty' && w !== 'loaded');

              if (hasOosFilter && !spot.data?.oos) return false;
              if (hasEmptyFilter && spot.status !== 'empty') return false;
              if (hasLoadedFilter && spot.status !== 'loaded') return false;

              if (remainingWords.length > 0) {
                const searchableText = [
                  spot.displayNumber,
                  spot.driverName || '',
                  spot.trailerNumber || '',
                  spot.shipmentNumber || '',
                  spot.carrier_name || ''
                ].join(' ').toLowerCase();

                if (!remainingWords.every(word => searchableText.includes(word))) return false;
              }
            }
            return true;
          })
          // Maintain the established order from Step 1 - sort by order_number, then by displayNumber
          .sort((a, b) => {
            const orderDiff = (a.order_number || 0) - (b.order_number || 0);
            if (orderDiff !== 0) return orderDiff;
            // Secondary sort by displayNumber when order_number is the same (common when order_number is 0)
            return a.displayNumber.localeCompare(b.displayNumber, undefined, { numeric: true });
          })
      })).filter(group => group.spots.length > 0);
  }, [yardSpots, zones, filterStatus, searchTerm]);

  // Add state for staging trailers
  const [stagingTrailers, setStagingTrailers] = useState<StagingTrailer[]>([]);
  const [loadingStagingTrailers, setLoadingStagingTrailers] = useState<boolean>(false);
  const [stagingTrailersError, setStagingTrailersError] = useState<string | null>(null);
  
  // Add state for zone-based trailers
  const [zoneTrailers, setZoneTrailers] = useState<ZoneTrailer[]>([]);
  const [loadingZoneTrailers, setLoadingZoneTrailers] = useState<boolean>(false);
  const [zoneTrailersError, setZoneTrailersError] = useState<string | null>(null);
  
  // Add state for moving trailer from staging to yard spot
  const [showMoveToYardSpotModal, setShowMoveToYardSpotModal] = useState<boolean>(false);
  const [selectedStagingTrailer, setSelectedStagingTrailer] = useState<StagingTrailer | null>(null);
  const [selectedYardSpotForMove, setSelectedYardSpotForMove] = useState<string>('');
  const [selectedYardSpotUuid, setSelectedYardSpotUuid] = useState<string>('');
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string | null>(null);
  const [spotFilter, setSpotFilter] = useState<string>('');
  const [autoCompleteMoveToYardSpot, setAutoCompleteMoveToYardSpot] = useState<boolean>(false);
  const [assigningYardSpot, setAssigningYardSpot] = useState<boolean>(false);
  const [assignYardSpotError, setAssignYardSpotError] = useState<string | null>(null);
  
  // First, add state for the dock door assignment modal
  const [showMoveToDockModal, setShowMoveToDockModal] = useState<boolean>(false);
  const [selectedDockDoor, setSelectedDockDoor] = useState<string>('');
  const [dockFilter, setDockFilter] = useState<string>('');
  const [selectedShipmentPuid, setSelectedShipmentPuid] = useState<string>('');
  const [movingToDock, setMovingToDock] = useState<boolean>(false);
  const [moveToDockError, setMoveToDockError] = useState<string | null>(null);
  const [validatingShipment, setValidatingShipment] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [createdTask, setCreatedTask] = useState<any>(null);
  const [lockedSpots, setLockedSpots] = useState<Set<string>>(new Set());
  const [lockedZoneTrailers, setLockedZoneTrailers] = useState<Set<string>>(new Set());
  
  // Add state for destination selection (yard spot or facility zone)
  const [selectedDestinationType, setSelectedDestinationType] = useState<string>('yard_spot');
  const [selectedFacilityZone, setSelectedFacilityZone] = useState<string>('');
  
  // Add state for zone trailer details modal
  const [showZoneTrailerDetailsModal, setShowZoneTrailerDetailsModal] = useState<boolean>(false);
  const [selectedZoneTrailer, setSelectedZoneTrailer] = useState<ZoneTrailer | null>(null);
  
  // Trailer notes states (for zone trailers)
  const [trailerNotesValue, setTrailerNotesValue] = useState<string>('');
  const [isEditingTrailerNotes, setIsEditingTrailerNotes] = useState<boolean>(false);
  const [savingTrailerNotes, setSavingTrailerNotes] = useState<boolean>(false);
  
  // Trailer notes states (for yard spot trailers)
  const [spotTrailerNotesValue, setSpotTrailerNotesValue] = useState<string>('');
  const [isEditingSpotTrailerNotes, setIsEditingSpotTrailerNotes] = useState<boolean>(false);
  const [savingSpotTrailerNotes, setSavingSpotTrailerNotes] = useState<boolean>(false);
  
  // Shipment editing state
  const [newShipmentReference, setNewShipmentReference] = useState<string>('');
  const [shipmentReferenceError, setShipmentReferenceError] = useState<string>('');
  const [isUpdatingShipment, setIsUpdatingShipment] = useState<boolean>(false);
  const [showRemoveShipmentConfirm, setShowRemoveShipmentConfirm] = useState<boolean>(false);
  const [isRemovingShipment, setIsRemovingShipment] = useState<boolean>(false);
  
  // Add state for task details modal
  const [showTaskDetailsModal, setShowTaskDetailsModal] = useState<boolean>(false);
  const [selectedTaskForDetails, setSelectedTaskForDetails] = useState<any>(null);
  
  // Add state for unassigning trailer
  const [isUnassigningTrailer, setIsUnassigningTrailer] = useState<boolean>(false);
  
  // Add state for adding trailer to empty spot
  const [isAddingTrailer, setIsAddingTrailer] = useState<boolean>(false);
  const [addTrailerScac, setAddTrailerScac] = useState<string>('');
  const [addTrailerNumber, setAddTrailerNumber] = useState<string>('');
  const [addTrailerStatus, setAddTrailerStatus] = useState<'EMPTY' | 'LOADED'>('EMPTY');
  const [addTrailerError, setAddTrailerError] = useState<string | null>(null);
  const [showCreateAnywayButton, setShowCreateAnywayButton] = useState<boolean>(false);
  const [trailerSearchInput, setTrailerSearchInput] = useState<string>('');
  const [addTrailerShipmentNumber, setAddTrailerShipmentNumber] = useState<string>('');
  const [selectedExistingTrailer, setSelectedExistingTrailer] = useState<ExistingTrailer | null>(null);
  
  // Add state for active dock doors with zone information
  const [activeDockDoors, setActiveDockDoors] = useState<Array<{door: string, dockUuid: string, zoneUuid: string, zoneName: string, hasMoveFromTask?: boolean, facilityId?: string}>>([]);
  const [loadingDockDoors, setLoadingDockDoors] = useState<boolean>(false);
  
  // Save dimensions and scroll position
  useLayoutEffect(() => {
    // Save initial dimensions of containers
    if (stagingAreaRef.current && !stagingHeight) {
      setStagingHeight(stagingAreaRef.current.offsetHeight);
    }
    if (spotsContainerRef.current && !spotsHeight) {
      setSpotsHeight(spotsContainerRef.current.offsetHeight);
    }
    
    // Save scroll position continually
    const handleScroll = () => {
      if (!isRestoringScroll) {
        scrollY.current = window.scrollY;
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [stagingHeight, spotsHeight, isRestoringScroll]);
  
  // Restore scroll position after state updates
  useLayoutEffect(() => {
    if (scrollY.current > 0 && !isRestoringScroll) {
      setIsRestoringScroll(true);
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollY.current);
        // Wait a bit more for any layout shifts to settle
        setTimeout(() => setIsRestoringScroll(false), 50);
      });
    }
  }, [yardSpots, stagingTrailers]);




  
  // Handler functions
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  

  


  // Unified modal handlers
  const handleShowUnifiedDetails = (item: LocalYardSpot | ZoneTrailer, type: 'yard_spot' | 'zone_trailer') => {
    const unifiedItem = type === 'yard_spot' 
      ? createUnifiedItemFromSpot(item as LocalYardSpot, zones)
      : createUnifiedItemFromZoneTrailer(item as ZoneTrailer, zones);
    
    setSelectedUnifiedItem(unifiedItem);
    setShowUnifiedDetailsModal(true);
    
    // Set up notes for editing if available
    if (unifiedItem.active_lineage?.notes) {
      setSpotTrailerNotesValue(unifiedItem.active_lineage.notes);
    }
    
    // Set selectedSpot for yard spots so handleSaveSpotTrailerNotes works
    if (type === 'yard_spot') {
      setSelectedSpot(item as LocalYardSpot);
    }
  };

  const handleCloseUnifiedModal = () => {
    setShowUnifiedDetailsModal(false);
    setSelectedUnifiedItem(null);
    setSelectedSpot(null);
    // Reset add trailer form
    setAddTrailerScac('');
    setAddTrailerNumber('');
    setAddTrailerStatus('EMPTY');
    setAddTrailerError(null);
    setShowCreateAnywayButton(false);
    setTrailerSearchInput('');
    setAddTrailerShipmentNumber('');
    setSelectedExistingTrailer(null);
  };

  const handleClearExistingTrailer = () => {
    setSelectedExistingTrailer(null);
    setAddTrailerScac('');
    setAddTrailerNumber('');
    setAddTrailerStatus('EMPTY');
    setAddTrailerShipmentNumber('');
    setTrailerSearchInput('');
    setAddTrailerError(null);
    setShowCreateAnywayButton(false);
  };
  

  
  const handleShowZoneManage = () => {
    setShowZoneManageModal(true);
  };
  
  const handleZoneManageClose = () => {
    setShowZoneManageModal(false);
  };
  
  const handleZoneEditClose = () => {
    setShowZoneEditModal(false);
    setEditingZone(null);
  };
  
  const handleShowZoneEdit = (zone: Zone) => {
    setEditingZone({ ...zone });
    setShowZoneEditModal(true);
  };
  
  const handleAddNewZone = () => {
    if (!newZoneName.trim()) return;
    
    const newZone: Zone = {
      id: `zone-${Date.now()}`, // Generate temp ID
      name: newZoneName,
      color: newZoneColor
    };
    
    // TODO: Implement API call to add zone
    console.log('Add new zone:', newZone);
    
    // Reset form
    setNewZoneName('');
    setNewZoneColor(getRandomColor());
  };
  
  const handleSaveZone = () => {
    if (!editingZone) return;
    
    // TODO: Implement API call to save zone
    console.log('Save zone:', editingZone);
    setShowZoneEditModal(false);
  };
  
  const handleDeleteZone = (zoneId: string) => {
    // TODO: Implement API call to delete zone
    console.log('Delete zone:', zoneId);
  };
  
  // Use useMemo for activeSpots
  const activeSpots = useMemo(() => {
    return yardSpots.filter(spot => spot.status === 'in-use' && spot.driverId);
  }, [yardSpots]);
  
  // Use useMemo for upcomingSchedules
  const upcomingSchedules = useMemo(() => {
    return yardSpots
      .flatMap(spot => 
        (spot.scheduledTimes || []).map(time => ({
          ...time,
          spotId: spot.id,
          spotNumber: spot.displayNumber,
          zoneId: spot.zoneId
        }))
      )
      .sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
  }, [yardSpots]);

  // Group zone trailers by zone - memoized to prevent recalculation
  const trailersByZone = useMemo(() => {
    // Show all zones, not just ones with trailers
    return zones
      .sort((a, b) => a.name.localeCompare(b.name)) // Sort zones alphabetically
      .map(zone => ({
        zone: {
          ...zone,
          color: zone.color || '#2196F3' // Default to blue if no color is defined
        },
        trailers: showUnallocatedAssets
          ? zoneTrailers.filter(trailer => trailer.zone_id === zone.id)
              .filter(trailer => {
                if (searchTerm.trim()) {
                  const searchWords = searchTerm.toLowerCase().trim().split(/\s+/).filter(w => w.length > 0);
                  const hasOosFilter = searchWords.includes('oos');
                  const hasEmptyFilter = searchWords.includes('empty');
                  const hasLoadedFilter = searchWords.includes('loaded');
                  const remainingWords = searchWords.filter(w => w !== 'oos' && w !== 'empty' && w !== 'loaded');

                  if (hasOosFilter && !trailer.data?.oos) return false;
                  // EMPTY filter: trailer must have EMPTY current_status
                  if (hasEmptyFilter && trailer.current_status?.toUpperCase() !== 'EMPTY') return false;
                  // LOADED filter: trailer must have LOADED current_status
                  if (hasLoadedFilter && trailer.current_status?.toUpperCase() !== 'LOADED') return false;

                  if (remainingWords.length > 0) {
                    const searchableText = [
                      trailer.trailer_number || '',
                      trailer.driver_name || '',
                      trailer.puid || '',
                      trailer.carrier_name || ''
                    ].join(' ').toLowerCase();

                    if (!remainingWords.every(word => searchableText.includes(word))) return false;
                  }
                }
                return true;
              })
          : [] // Hide zone trailers when showUnallocatedAssets is false
      }));
    // Don't filter out empty zones - show all zones including empty ones
  }, [zoneTrailers, zones, searchTerm, showUnallocatedAssets]);

  // Determine if we should use zone-based or spot-based display
  // Use zone-based display when facilityZones data is available (even if no trailers)
  const useZoneBasedDisplay = facilityZones && facilityZones.length > 0;


  
  // Update the handleEndAssignment function to use localModifications

  // Helper function to update spot to available
  const updateSpotToAvailable = (spotId: string) => {
    console.log(`Clearing yard spot ${spotId}, removing carrier_name and other data`);
    
    // Update local modifications
    setLocalModifications(prev => ({
      ...prev,
      [spotId]: { 
        status: 'available' as Status, 
        driverId: null, 
        driverName: null, 
        trailerId: null, 
        trailerNumber: null, 
        shipmentNumber: null,
        carrier_name: null // Explicitly clear carrier_name
      }
    }));
    
    // Update the selectedSpot if it's currently selected
    if (selectedSpot && selectedSpot.id === spotId) {
      setSelectedSpot({
        ...selectedSpot,
        status: 'available' as Status, 
        driverId: null, 
        driverName: null, 
        trailerId: null, 
        trailerNumber: null, 
        shipmentNumber: null,
        carrier_name: null // Explicitly clear carrier_name
      });
    }
    
    console.log(`Ended assignment for spot ${spotId}`);
  };
  


  // Add handleMoveToYardSpot function to open the modal
  const handleMoveToYardSpot = (trailer: StagingTrailer) => {
    setSelectedStagingTrailer(trailer);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setSelectedZoneFilter(null);
    setSpotFilter('');
    setAssignYardSpotError(null);
    // Pre-populate shipment reference from the trailer's shipment_number field
    setSelectedShipmentPuid(trailer.shipment_number || '');
    setShowMoveToYardSpotModal(true);
  };

  // Add handleCloseMoveToYardSpotModal function to close the modal
  const handleCloseMoveToYardSpotModal = () => {
    setShowMoveToYardSpotModal(false);
    setSelectedStagingTrailer(null);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setSelectedZoneFilter(null);
    setSpotFilter('');
    setAssignYardSpotError(null);
    setSelectedShipmentPuid('');
    setSelectedDestinationType('yard_spot');
    setSelectedFacilityZone('');
    setAutoCompleteMoveToYardSpot(false); // Reset auto-complete checkbox
  };

  // Update handleAssignYardSpot to use localModifications without triggering unnecessary re-renders
  const handleAssignYardSpot = async () => {
    if (!selectedStagingTrailer) {
      setAssignYardSpotError('No trailer selected');
      return;
    }
    
    if (selectedDestinationType === 'yard_spot' && !selectedYardSpotForMove) {
      setAssignYardSpotError('Please select a yard spot');
      return;
    }
    
    if (selectedDestinationType === 'facility_zone' && !selectedFacilityZone) {
      setAssignYardSpotError('Please select a facility zone');
      return;
    }

    setAssigningYardSpot(true);
    setAssignYardSpotError(null);
    
    let shipmentId = null;
    
    // Only validate reference number if one is provided
    if (selectedShipmentPuid && selectedShipmentPuid.trim()) {
      setValidatingShipment(true);
      
      try {
        // Get auth token from cookie or localStorage
        const authToken = getAuthToken();

        // Validate that the shipment reference number exists
        const shipmentValidationQuery = `
          query GetShipmentIdByPUID {
            shipment_without_trailer_by_reference_number(reference_number: "${selectedShipmentPuid.trim()}", facility_id: "${facilityOfTrailer(selectedStagingTrailer.trailer_uuid, selectedStagingTrailer.puid)}", trailer_uuid: "${selectedStagingTrailer.trailer_uuid || ""}") {
              id
            }
          }
        `;

        const validationResponse = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: shipmentValidationQuery
          })
        });

        const validationResult = await validationResponse.json();
        
        if (validationResult.errors) {
          // Check if the error is "Shipment not found"
          const shipmentNotFoundError = validationResult.errors.find(
            (error: any) => error.message === "Shipment not found" && 
            error.path && error.path.includes("shipment_without_trailer_by_reference_number")
          );
          
          if (shipmentNotFoundError) {
            setAssignYardSpotError(`No shipment found with reference number "${selectedShipmentPuid}". Please verify the reference number.`);
            setValidatingShipment(false);
            setAssigningYardSpot(false);
            return;
          }
          
          // For other errors, throw them
          throw new Error(validationResult.errors[0].message);
        }

        // Check if shipment was found
        const shipment = validationResult.data?.shipment_without_trailer_by_reference_number;
        if (!shipment || !shipment.id) {
          setAssignYardSpotError(`No shipment found with reference number "${selectedShipmentPuid}". Please verify the reference number.`);
          setValidatingShipment(false);
          setAssigningYardSpot(false);
          return;
        }

        console.log('Shipment validation successful:', shipment);
        shipmentId = shipment.id;
        setValidatingShipment(false);
      } catch (err) {
        console.error('Error validating shipment:', err);
        setAssignYardSpotError('Failed to validate reference number. Please try again.');
        setValidatingShipment(false);
        setAssigningYardSpot(false);
        return;
      }
    }
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Get the trailer UUID - prefer trailer_uuid if available, otherwise use puid
      const trailerUuid = selectedStagingTrailer.trailer_uuid || selectedStagingTrailer.puid;
      
      if (!trailerUuid) {
        throw new Error('No trailer UUID available');
      }

      // GraphQL mutation for creating movement task
      const createMovementTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $destination_facility_id: ID,
          $facility_zone_uuid: String,
          $yard_spot_uuid: String,
          $source_facility_zone_uuid: String,
          $destination_facility_zone_uuid: String,
          $driver_id: String,
          $shipment_id: String,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            destination_facility_id: $destination_facility_id
            facility_zone_uuid: $facility_zone_uuid
            yard_spot_uuid: $yard_spot_uuid
            source_facility_zone_uuid: $source_facility_zone_uuid
            destination_facility_zone_uuid: $destination_facility_zone_uuid
            driver_id: $driver_id
            shipment_id: $shipment_id
            force: $force
            auto_complete: $auto_complete
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

      // Determine source type and movement type
      const isFromYardSpot = selectedStagingTrailer.puid.startsWith('spot-') || 
                            yardSpots.some(spot => spot.id === selectedStagingTrailer.puid || 
                                                  spot.shipmentNumber === selectedStagingTrailer.puid ||
                                                  spot.trailerId === selectedStagingTrailer.trailer_uuid);
      
      // Improved facility zone detection: check if trailer exists in zone trailers or has zone-trailer puid
      const isFromFacilityZone = selectedStagingTrailer.puid.startsWith('zone-trailer-') ||
                                 zoneTrailers.some(zt => zt.trailer_uuid === selectedStagingTrailer.trailer_uuid ||
                                                        zt.trailer_number === selectedStagingTrailer.trailer_number);
      
      let movementType: string;
      let variables: any = {
        trailer_uuid: trailerUuid,
        facility_id: facilityOfTrailer(trailerUuid, selectedStagingTrailer.puid),
        driver_id: selectedStagingTrailer.driver_id || undefined,
        shipment_id: shipmentId || undefined,
        force: false
      };

      if (selectedDestinationType === 'yard_spot') {
        // Moving to yard spot - use UUID for exact match
        const destinationSpot = moveDestinations.yardSpots.find(spot => spot.id === selectedYardSpotUuid);
        
        if (!destinationSpot) {
          throw new Error(`Yard spot with ID ${selectedYardSpotUuid} not found`);
        }

        variables.facility_zone_uuid = destinationSpot.zoneId;
        variables.yard_spot_uuid = destinationSpot.id;

        if (isFromYardSpot) {
          movementType = 'yard_to_yard';
          // Find source yard spot for yard-to-yard movement
          const sourceSpot = yardSpots.find(spot => 
            spot.id === selectedStagingTrailer.puid || 
            spot.shipmentNumber === selectedStagingTrailer.puid ||
            spot.trailerId === selectedStagingTrailer.trailer_uuid
          );
          if (sourceSpot) {
            variables.source_facility_zone_uuid = sourceSpot.zoneId;
          }
        } else if (isFromFacilityZone) {
          movementType = 'facility_zone_to_yard_spot';
          // Find source facility zone
          const sourceZoneTrailer = zoneTrailers.find(zt => 
            zt.trailer_uuid === trailerUuid || zt.puid === selectedStagingTrailer.puid
          );
          if (sourceZoneTrailer) {
            variables.source_facility_zone_uuid = sourceZoneTrailer.zone_id;
          }
        } else {
          // Default to yard_to_yard for compatibility
          movementType = 'yard_to_yard';
        }
      } else {
        // Moving to facility zone
        movementType = isFromYardSpot ? 'yard_spot_to_facility_zone' : 
                      isFromFacilityZone ? 'facility_zone_to_facility_zone' : 
                      'yard_spot_to_facility_zone'; // Default fallback

        // Always set destination_facility_zone_uuid for facility zone destinations
        variables.destination_facility_zone_uuid = selectedFacilityZone;

        if (isFromYardSpot) {
          // Find source yard spot
          const sourceSpot = yardSpots.find(spot => 
            spot.id === selectedStagingTrailer.puid || 
            spot.shipmentNumber === selectedStagingTrailer.puid ||
            spot.trailerId === selectedStagingTrailer.trailer_uuid
          );
          if (sourceSpot) {
            variables.yard_spot_uuid = sourceSpot.id;
            // For yard_spot_to_facility_zone, also set the source facility zone (the zone containing the yard spot)
            variables.facility_zone_uuid = sourceSpot.zoneId;
          }
        } else if (isFromFacilityZone) {
          // Find source facility zone for facility_zone_to_facility_zone movement
          const sourceZoneTrailer = zoneTrailers.find(zt => 
            zt.trailer_uuid === trailerUuid || zt.puid === selectedStagingTrailer.puid
          );
          if (sourceZoneTrailer) {
            variables.source_facility_zone_uuid = sourceZoneTrailer.zone_id;
          }
          // Ensure facility_zone_uuid is not conflicting with destination_facility_zone_uuid
          variables.facility_zone_uuid = null;
        }
      }

      variables.movement_type = movementType;
      
      // Add auto_complete if checked
      if (autoCompleteMoveToYardSpot) {
        variables.auto_complete = true;
      }

      console.log('Creating movement task to yard spot with variables:', variables);

      // Execute the GraphQL mutation
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createMovementTaskMutation,
          variables: withCampusFacilities(
            variables,
            variables.facility_id,
            moveDestinations.facilityOf(
              selectedDestinationType === 'yard_spot' ? 'yard_spot' : 'facility_zone',
              selectedDestinationType === 'yard_spot' ? selectedYardSpotUuid : selectedFacilityZone,
            ),
          )
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0].message);
      }

      // Check if the movement task was created successfully
      if (!result.data?.create_movement_task?.uuid) {
        throw new Error('Failed to create movement task to yard spot');
      }

      console.log('Movement task created successfully:', result.data.create_movement_task);
      
      // Handle locking based on destination type
      if (selectedDestinationType === 'yard_spot') {
        const destinationSpot = yardSpots.find(spot => spot.id === selectedYardSpotUuid);
        if (destinationSpot) {
          // Lock the destination spot to show it's in a pending state
          setLockedSpots(prev => new Set(prev).add(destinationSpot.id));
        }
      }
      
      // Handle source cleanup based on movement type
      if (isFromYardSpot) {
        // If moving from yard spot, clear the source spot
        const sourceSpot = yardSpots.find(spot => 
          spot.id === selectedStagingTrailer.puid || 
          spot.shipmentNumber === selectedStagingTrailer.puid ||
          spot.trailerId === selectedStagingTrailer.trailer_uuid
        );
        if (sourceSpot) {
          updateSpotToAvailable(sourceSpot.id);
        }
      } else if (isFromFacilityZone) {
        // Remove the trailer from zone trailers since it's now moving
        setZoneTrailers(prev => 
          prev.filter(trailer => trailer.puid !== selectedStagingTrailer.puid)
        );
      } else {
        // Remove from staging trailers (fallback case)
        setStagingTrailers(prev => 
          prev.filter(trailer => trailer.puid !== selectedStagingTrailer.puid)
        );
      }
      
      // Store the task data and show success modal
      setCreatedTask(result.data.create_movement_task);
      setShowSuccessModal(true);
      
      // Close the modal
      handleCloseMoveToYardSpotModal();
      
      // If auto-complete is enabled, reload the page to reflect changes immediately
      // Otherwise, refresh data after a delay to show the locked state
      if (autoCompleteMoveToYardSpot) {
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        setTimeout(() => {
          refreshData();
        }, 2000);
      }
    } catch (err) {
      console.error('Error creating movement task to yard spot:', err);
      setAssignYardSpotError(
        err instanceof Error && err.message ? err.message : 'Failed to move trailer to yard spot. Please try again.',
      );
    } finally {
      setAssigningYardSpot(false);
    }
  };

  // Extract zone trailers from the existing yard data
  const extractZoneTrailers = useCallback(() => {
    if (!selectedFacility || zones.length === 0 || !facilityZones) return;
    
    setLoadingZoneTrailers(true);
    setZoneTrailersError(null);
    
    try {
      // Extract trailers from the facility_zones data that comes from the existing yard query
      const extractedTrailers: ZoneTrailer[] = [];
      
      facilityZones.forEach((facilityZone: any) => {
        // Process trailers if they exist, but we'll still show the zone even if empty
        if (facilityZone.trailers && facilityZone.trailers.length > 0) {
          facilityZone.trailers.forEach((trailer: any) => {
            const driver = trailer.active_lineage?.trailer_state?.driver;
            const driverName = driver ? `${driver.firstName || ''} ${driver.lastName || ''}`.trim() : '';
            
            extractedTrailers.push({
              puid: '', // Not available in GraphQL response
              driver_id: driver?.id || '', // Extract driver ID from trailer state
              driver_name: driverName, // Construct driver name from first and last name
              driver_phone_number: '', // Not available in GraphQL response
              truck_number: '', // Not available in GraphQL response
              trailer_number: trailer.trailerNumber || '',
              direction: trailer.active_lineage?.trailer_state?.shipment?.direction || '',
              carrier_name: trailer.carrier?.name || '',
              zone_id: facilityZone.facility_zone_uuid,
              current_status: trailer.status?.name?.toUpperCase() || '', // Now available from GraphQL
              trailer_uuid: trailer.uuid || '',
              shipment_number: trailer.active_lineage?.trailer_state?.shipment?.referenceNumber || '', // Add shipment number if available
              shipment_id: trailer.active_lineage?.trailer_state?.shipment?.id || null, // Add shipment ID to preserve relationship
              data: trailer.data || null, // Include trailer data for OOS access
              active_lineage: trailer.active_lineage || undefined // Include active_lineage with notes
            });
          });
        }
      });

      setZoneTrailers(extractedTrailers);
      
      // Extract staging trailers from zone trailers (only if staging zone actually exists)
      // Every staging zone: in a campus roll-up each facility has its own
      const stagingZoneIds = zones.filter(zone => zone.zone_type === "staging").map(zone => zone.id);
      if (stagingZoneIds.length > 0) {
        const stagingTrailersFromZones = extractedTrailers
          .filter(trailer => stagingZoneIds.includes(trailer.zone_id))
          .map(trailer => ({
            puid: trailer.puid,
            driver_id: trailer.driver_id,
            driver_name: trailer.driver_name,
            driver_phone_number: trailer.driver_phone_number || '',
            truck_number: trailer.truck_number || '',
            trailer_number: trailer.trailer_number,
            direction: trailer.direction,
            carrier_name: trailer.carrier_name || '',
            trailer_uuid: trailer.trailer_uuid,
            current_status: trailer.current_status
          }));
        
        setStagingTrailers(stagingTrailersFromZones);
      } else {
        // Clear staging trailers if no staging zone exists
        setStagingTrailers([]);
      }
      
    } catch (err) {
      console.error('Error extracting zone trailers:', err);
      setZoneTrailersError('Failed to load zone trailers');
    } finally {
      setLoadingZoneTrailers(false);
      setLoadingStagingTrailers(false);
    }
  }, [selectedFacility, zones, facilityZones]);

  // Extract staging trailers from yard data or extract zone trailers
  useEffect(() => {
    if (selectedFacility && zones.length > 0) {
      setLoadingStagingTrailers(true);
      // Extract zone-based trailers from existing data
      extractZoneTrailers();
    }
  }, [selectedFacility, zones, extractZoneTrailers]);
  
  // Only call refresh on manual refresh
  const handleRefreshData = useCallback(() => {
    refreshData();
    extractZoneTrailers();
  }, [refreshData, extractZoneTrailers]);

  // Process warehouse data to extract dock doors
  // Campus roll-up: every trailer action uses the facility the trailer (or spot) is at as its source.
  const anchorFacilityId = selectedFacility?.id !== undefined ? String(selectedFacility.id) : '1';
  const facilityOfZone = (zoneId?: string | null): string =>
    (zoneId && facilityZones.find((zone: any) => zone.facility_zone_uuid === zoneId)?.facilityId) || anchorFacilityId;
  const facilityOfTrailer = (trailerUuid?: string | null, puid?: string | null): string => {
    const spot = rawYardSpots.find(
      s => (!!trailerUuid && s.trailerId === trailerUuid) || (!!puid && (s.id === puid || s.shipmentNumber === puid)),
    );
    if (spot?.facilityId) return spot.facilityId;
    const zoneTrailer = zoneTrailers.find(zt => !!trailerUuid && zt.trailer_uuid === trailerUuid);
    return facilityOfZone(zoneTrailer?.zone_id);
  };
  // The staging zone of a facility (single-facility zones carry no facilityId).
  const stagingZoneFor = (facilityId: string) =>
    zones.find((zone: Zone) => zone.zone_type === 'staging' && (!zone.facilityId || zone.facilityId === facilityId));

  // Move modals offer the whole campus of the trailer's facility, even when the page is narrowed.
  const moveSourceFacilityId = showMoveToDockModal
    ? selectedSpot?.facilityId || facilityOfTrailer(selectedSpot?.trailerId)
    : facilityOfTrailer(selectedStagingTrailer?.trailer_uuid, selectedStagingTrailer?.puid);
  const moveDestinations = useModalDestinations({
    sourceFacilityId: moveSourceFacilityId,
    enabled: showMoveToYardSpotModal || showMoveToDockModal,
    fallback: {
      yardSpots,
      zones,
      facilityZones: facilityZones || [],
      dockDoors: activeDockDoors,
      lockedSpots: mergeLockSets(taskBasedLockedSpots, lockedSpots),
    },
  });

  const processWarehouseDockDoors = useCallback(() => {
    if (!warehouseData) {
      setActiveDockDoors([]);
      return;
    }
    
    setLoadingDockDoors(true);
    
    try {
      // Get list of all dock doors with zone information from the warehouse data
      let allDockDoors: Array<{door: string, dockUuid: string, zoneUuid: string, zoneName: string, hasMoveFromTask?: boolean}> = [];
      let availableDoors: Array<{door: string, dockUuid: string, zoneUuid: string, zoneName: string, hasMoveFromTask?: boolean, facilityId?: string}> = [];
      
      if (warehouseData.warehouseZones) {
        warehouseData.warehouseZones.forEach((zone: any) => {
          // Campus roll-up: no moves into docks at facilities the user is not assigned to
          if (zone.canDock === false) return;
          if (zone.docks && zone.docks.length > 0) {
            zone.docks.forEach((dock: any) => {
              // Add all dock doors to the list with zone info and dock UUID
              allDockDoors.push({door: dock.dockName, dockUuid: dock.dockUuid, zoneUuid: zone.zoneUuid, zoneName: zone.zoneName});
              
              if (isDockUnavailableForMovement(dock.configuration)) {
                return;
              }

              // Check availability logic:
              // 1. If dock door has lock → NOT Available (all cases)
              // 2. Else if no trailer → Available (no move-from task)
              // 3. Else if has trailer AND trailer has lock → Available (but mark as having move-from task)
              // 4. Else (has trailer AND no lock on trailer) → NOT Available

              // First check if door itself has a lock
              const doorHasLock = allFacilityLocks.some((lock: any) => {
                const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                return lockUuid === dock.dockUuid && 
                       (lock.model_name === 'Dock' || lock.resource?.__typename === 'DockObject');
              });
              
              if (doorHasLock) {
                // Door is locked, NOT available
                return;
              }
              
              const hasTrailer = !!dock.trailer_uuid;
              
              if (!hasTrailer) {
                // No trailer, door is available (no move-from task)
                availableDoors.push({door: dock.dockName, dockUuid: dock.dockUuid, zoneUuid: zone.zoneUuid, zoneName: zone.zoneName, hasMoveFromTask: false, facilityId: zone.facilityId});
              } else {
                // Has trailer - check if trailer has a lock (being moved out)
                const trailerHasLock = allFacilityLocks.some((lock: any) => {
                  const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                  return lockUuid === dock.trailer_uuid && 
                         (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                });
                
                if (trailerHasLock) {
                  // Trailer is being moved out, door will be available (but has move-from task)
                  availableDoors.push({door: dock.dockName, dockUuid: dock.dockUuid, zoneUuid: zone.zoneUuid, zoneName: zone.zoneName, hasMoveFromTask: true, facilityId: zone.facilityId});
                }
                // else: trailer is stuck (no lock), door is NOT available
              }
            });
          }
        });
      }
      

      // Use only the available dock doors from warehouse data
      if (availableDoors.length > 0) {
        // Sort the available doors naturally (D1, D2, D10, D11, etc.)
        const sortedAvailableDoors = availableDoors.sort((a, b) => {
          const numA = parseInt(a.door.replace(/\D/g, ''));
          const numB = parseInt(b.door.replace(/\D/g, ''));
          return numA - numB;
        });
        setActiveDockDoors(sortedAvailableDoors);
      } else {
        // If no available doors, show empty array (don't show occupied/inactive doors)
        setActiveDockDoors([]);
        console.log("No available dock doors found");
      }
    } catch (err) {
      console.error('Error processing warehouse dock doors:', err);
      // Fallback to empty array in case of error
      setActiveDockDoors([]);
    } finally {
      setLoadingDockDoors(false);
    }
  }, [warehouseData, allFacilityLocks]);

  // Process warehouse data when it changes
  useEffect(() => {
    processWarehouseDockDoors();
  }, [processWarehouseDockDoors]);

  // Handle move to dock directly from tile
  const handleMoveToDockFromTile = (spot: LocalYardSpot) => {
    // Check if trailer is OOS before allowing move to dock
    if (isTrailerOOS(spot)) {
      setMoveToDockError('Cannot move trailer to dock: Trailer is out of service (OOS)');
      return;
    }
    
    setSelectedSpot(spot);
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid(spot.shipmentNumber || '');
    setMoveToDockError(null);
    setShowMoveToDockModal(true);
    
    // Process warehouse data for dock doors (data is already available)
    processWarehouseDockDoors();
  };

  // Handle move spot to spot directly from tile
  const handleMoveSpotToSpot = (spot: LocalYardSpot) => {
    // Convert spot to StagingTrailer format for compatibility with existing modal
    const stagingTrailer: StagingTrailer = {
      puid: spot.shipmentNumber || spot.id,
      driver_id: spot.driverId || '',
      driver_name: spot.driverName || '',
      driver_phone_number: '',
      truck_number: '',
      trailer_number: spot.trailerNumber || '',
      direction: spot.direction || '',
      carrier_name: spot.carrier_name || '',
      trailer_uuid: spot.trailerId || spot.id,
      current_status: spot.status === 'loaded' ? 'LOADED' : undefined
    };
    
    setSelectedStagingTrailer(stagingTrailer);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setAssignYardSpotError(null);
    // Pre-populate shipment reference from the spot's shipment number
    setSelectedShipmentPuid(spot.shipmentNumber || '');
    setShowMoveToYardSpotModal(true);
  };

  // Handle move zone trailer to yard spot
  const handleMoveZoneTrailerToYardSpot = (trailer: ZoneTrailer) => {
    // Convert ZoneTrailer to StagingTrailer format for compatibility with existing modal
    const stagingTrailer: StagingTrailer = {
      puid: trailer.puid,
      driver_id: trailer.driver_id,
      driver_name: trailer.driver_name,
      driver_phone_number: trailer.driver_phone_number || '',
      truck_number: trailer.truck_number || '',
      trailer_number: trailer.trailer_number,
      direction: trailer.direction,
      carrier_name: trailer.carrier_name || '',
      trailer_uuid: trailer.trailer_uuid, // Pass the trailer UUID
      current_status: trailer.current_status // Pass the current status for loaded detection
    };
    
    setSelectedStagingTrailer(stagingTrailer);
    setSelectedYardSpotForMove('');
    setSelectedYardSpotUuid('');
    setAssignYardSpotError(null);
    // Pre-populate shipment reference from the trailer's shipment reference number
    setSelectedShipmentPuid(trailer.shipment_number || trailer.puid || '');
    setShowMoveToYardSpotModal(true);
  };

  // Handle move zone trailer directly to dock
  const handleMoveZoneTrailerToDock = (trailer: ZoneTrailer) => {
    // Check if trailer is OOS before allowing move to dock
    if (isTrailerOOS(trailer)) {
      setMoveToDockError('Cannot move trailer to dock: Trailer is out of service (OOS)');
      return;
    }
    
    // Convert ZoneTrailer to LocalYardSpot format for compatibility with existing modal
    const spotData: LocalYardSpot = {
      id: `zone-trailer-${trailer.trailer_uuid || trailer.puid}`,
      number: 0, // Use 0 as default since zone trailers don't have spot numbers
      displayNumber: `Zone-${trailer.trailer_number}`,
      status: 'in-use' as Status,
      zoneId: trailer.zone_id,
      trailerNumber: trailer.trailer_number,
      driverName: trailer.driver_name,
      shipmentNumber: trailer.shipment_number || trailer.puid,
      carrier_name: trailer.carrier_name || null,
      // Add other required fields with defaults
      order_number: 0,
      driverId: trailer.driver_id,
      trailerId: trailer.trailer_uuid || trailer.puid, // Use trailer UUID if available, fallback to puid
      data: trailer.data, // Include data field for OOS access
      scheduledTimes: [],
      current_status: trailer.current_status
    };
    
    setSelectedSpot(spotData);
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid(trailer.shipment_number || trailer.puid || '');
    setMoveToDockError(null);
    setShowMoveToDockModal(true);
    
    // Process warehouse data for dock doors (data is already available)
    processWarehouseDockDoors();
    };

  // Handle closing zone trailer details modal
  const handleCloseZoneTrailerDetailsModal = () => {
    setShowZoneTrailerDetailsModal(false);
    setSelectedZoneTrailer(null);
  };

  // Get all existing trailers in the yard (yard spots and zones, but NOT warehouse/dock doors)
  const getAllExistingTrailers = useCallback(() => {
    const trailers: Array<{scac: string, trailerNumber: string, trailerUuid: string, location: string, isAtDock?: boolean, shipmentNumber?: string, trailerStatus?: 'EMPTY' | 'LOADED' | string}> = [];
    
    // Get trailers from yard spots (only if they have valid data)
    yardSpots.forEach(spot => {
      if (spot.trailerNumber && spot.trailerId && spot.trailerNumber.trim()) {
        trailers.push({
          scac: spot.carrier_name || '',
          trailerNumber: spot.trailerNumber.trim(),
          trailerUuid: spot.trailerId,
          location: `Yard Spot ${spot.displayNumber}`,
          isAtDock: false,
          shipmentNumber: spot.shipmentNumber || undefined,
          trailerStatus: spot.trailerStatus || undefined
        });
      }
    });
    
    // Get trailers from facility zones (only if they have valid data)
    zoneTrailers.forEach(trailer => {
      if (trailer.trailer_number && trailer.trailer_uuid && trailer.trailer_number.trim()) {
        const zone = zones.find(z => z.id === trailer.zone_id);
        trailers.push({
          scac: trailer.carrier_name || '',
          trailerNumber: trailer.trailer_number.trim(),
          trailerUuid: trailer.trailer_uuid,
          location: `Zone: ${zone?.name || 'Unknown'}`,
          isAtDock: false,
          shipmentNumber: trailer.shipment_number || undefined,
          trailerStatus: trailer.current_status || undefined
        });
      }
    });
    
    // NOTE: We intentionally do NOT include warehouse/dock door trailers here
    // If a user types a trailer that exists in the warehouse, we want to create a new lineage
    // rather than moving the existing trailer from the dock
    
    // Deduplicate by trailerUuid
    const uniqueTrailers = trailers.filter((trailer, index, self) => 
      index === self.findIndex(t => t.trailerUuid === trailer.trailerUuid)
    );
    
    return uniqueTrailers;
  }, [yardSpots, zoneTrailers, zones, warehouseData]);

  // Handle adding trailer to empty spot
  const handleAddTrailerToSpot = async (forceCreate: boolean = false) => {
    if (!selectedUnifiedItem || selectedUnifiedItem.type !== 'yard_spot') return;
    
    const spot = selectedUnifiedItem.originalData as LocalYardSpot;
    
    if (!addTrailerScac.trim() || !addTrailerNumber.trim()) {
      setAddTrailerError('Please enter both SCAC and trailer number (e.g., "ABCD 12345")');
      return;
    }
    
    setIsAddingTrailer(true);
    setAddTrailerError(null);
    
    try {
      // Get auth token
      const authToken = getAuthToken();
      
      // Check if trailer already exists in the yard spots or zones (but NOT warehouse)
      const existingTrailers = getAllExistingTrailers();
      const existingTrailer = existingTrailers.find(t => 
        t.scac.toLowerCase() === addTrailerScac.trim().toLowerCase() &&
        t.trailerNumber.toLowerCase() === addTrailerNumber.trim().toLowerCase()
      );
      
      if (existingTrailer) {
        // Trailer exists in yard or zone - create movement task to move it to this spot
        const movementType = 'yard_to_yard'; // Simplified for now
        
        // Get the shipment number from the trailer (was captured when selecting from autocomplete)
        const shipmentReferenceNumber = addTrailerShipmentNumber || existingTrailer.shipmentNumber;
        let shipmentId = null;
        
        // If we have a shipment reference, look up the shipment ID
        if (shipmentReferenceNumber && shipmentReferenceNumber.trim()) {
          try {
            const shipmentValidationQuery = `
              query GetShipmentIdByPUID {
                shipment_by_reference_number(reference_number: "${shipmentReferenceNumber.trim()}", facility_id: "${facilityOfTrailer(existingTrailer.trailerUuid)}") {
                  id
                }
              }
            `;

            const validationResponse = await fetch(GRAPHQL_API_URL, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                query: shipmentValidationQuery
              })
            });

            const validationResult = await validationResponse.json();
            
            // If shipment found, use its ID
            if (validationResult.data?.shipment_by_reference_number?.id) {
              shipmentId = validationResult.data.shipment_by_reference_number.id;
            }
          } catch (err) {
            console.error('Error looking up shipment:', err);
            // Continue without shipment ID if lookup fails
          }
        }
        
        const moveVariables: any = {
          trailer_uuid: existingTrailer.trailerUuid,
          movement_type: movementType,
          facility_id: facilityOfTrailer(existingTrailer.trailerUuid),
          yard_spot_uuid: spot.id,
          facility_zone_uuid: spot.zoneId,
          force: false,
          auto_complete: true,
          shipment_id: shipmentId || undefined
        };
        
        const moveMutation = `
          mutation CreateMovementTask(
            $trailer_uuid: String!,
            $movement_type: String!,
            $facility_id: ID!,
            $destination_facility_id: ID,
            $yard_spot_uuid: String,
            $facility_zone_uuid: String,
            $force: Boolean,
            $auto_complete: Boolean,
            $shipment_id: String
          ) {
            create_movement_task(input: {
              trailer_uuid: $trailer_uuid
              movement_type: $movement_type
              facility_id: $facility_id
              destination_facility_id: $destination_facility_id
              yard_spot_uuid: $yard_spot_uuid
              facility_zone_uuid: $facility_zone_uuid
              force: $force
              auto_complete: $auto_complete
              shipment_id: $shipment_id
            }) {
              uuid
              task_status {
                status
              }
            }
          }
        `;
        
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: moveMutation,
            variables: withCampusFacilities(moveVariables, moveVariables.facility_id, spot.facilityId)
          })
        });
        
        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0]?.message || 'Failed to move trailer');
        }
        
        alert(`Trailer ${addTrailerNumber} moved to ${spot.displayNumber}`);
        
      } else {
        // Trailer doesn't exist in yard/zones - create new trailer at this spot
        const addVariables: any = {
          scac: addTrailerScac.trim(),
          trailerNumber: addTrailerNumber.trim(),
          facilityId: spot.facilityId || selectedFacility?.id?.toString() || "1",
          status: addTrailerStatus,
          yardSpotUuid: spot.id,
          facilityZoneUuid: spot.zoneId,
          notes: null,
          force_create_trailer_if_active: forceCreate
        };
        
        const addMutation = `
          mutation AddTrailer(
            $scac: String!,
            $trailerNumber: String!,
            $facilityId: ID!,
            $status: String!,
            $notes: String,
            $facilityZoneUuid: String,
            $yardSpotUuid: String,
            $force_create_trailer_if_active: Boolean
          ) {
            addTrailer(input: {
              scac: $scac
              trailerNumber: $trailerNumber
              facilityId: $facilityId
              status: $status
              notes: $notes
              facilityZoneUuid: $facilityZoneUuid
              yardSpotUuid: $yardSpotUuid
              force_create_trailer_if_active: $force_create_trailer_if_active
            }) {
              success
              trailer {
                uuid
                trailerNumber
              }
              errors
            }
          }
        `;
        
        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: addMutation,
            variables: addVariables
          })
        });
        
        const result = await response.json();
        
        if (result.errors) {
          throw new Error(result.errors[0]?.message || 'Failed to add trailer');
        }
        
        const trailerData = result.data?.addTrailer;
        if (!trailerData?.success) {
          throw new Error(trailerData?.errors?.join(', ') || 'Failed to add trailer');
        }
        
        alert(`Trailer ${addTrailerNumber} added to ${spot.displayNumber}`);
      }
      
      // Reset form and reload page
      setAddTrailerScac('');
      setAddTrailerNumber('');
      setAddTrailerStatus('EMPTY');
      setAddTrailerShipmentNumber('');
      handleCloseUnifiedModal();
      window.location.reload();
      
    } catch (error: any) {
      console.error('Error adding trailer to spot:', error);
      const errorMessage = error.message || 'Failed to add trailer';
      setAddTrailerError(errorMessage);
      
      // Check if error contains "is already active"
      if (errorMessage.toLowerCase().includes('is already active')) {
        setShowCreateAnywayButton(true);
      }
      
      setIsAddingTrailer(false);
    }
  };

  // Handle create anyway (force create despite "is already active" error)
  const handleCreateAnyway = () => {
    setShowCreateAnywayButton(false);
    handleAddTrailerToSpot(true);
  };

  // Handle unassigning trailer (send to staging zone)
  const handleUnassignTrailer = async () => {
    if (!selectedUnifiedItem || !selectedUnifiedItem.trailerNumber) return;
    
    // Confirm action
    const confirmed = window.confirm(`Are you sure you want to unassign trailer ${selectedUnifiedItem.trailerNumber}? It will be moved to the staging zone.`);
    if (!confirmed) return;
    
    setIsUnassigningTrailer(true);
    
    try {
      // Get auth token
      const authToken = getAuthToken();
      
      // The staging zone of the facility the trailer is at (a spot carries its facility;
      // unified yard-spot items have no trailerUuid).
      const sourceSpot =
        selectedUnifiedItem.type === 'yard_spot' ? (selectedUnifiedItem.originalData as LocalYardSpot) : null;
      const sourceFacilityId = sourceSpot
        ? sourceSpot.facilityId || facilityOfTrailer(sourceSpot.trailerId)
        : facilityOfTrailer(selectedUnifiedItem.trailerUuid);
      const stagingZone = stagingZoneFor(sourceFacilityId);
      
      if (!stagingZone) {
        throw new Error('Staging zone not found. Please ensure a facility zone with zone_type "staging" exists.');
      }
      
      // Determine movement type and variables based on source
      let movementType: string;
      let trailerUuid: string;
      let shipmentId: string | null = null;
      let driverId: string | null = null;
      const variables: any = {
        facility_id: sourceFacilityId,
        destination_facility_zone_uuid: stagingZone.id,
        force: false,
        auto_complete: true  // Auto-complete the task
      };
      
      if (selectedUnifiedItem.type === 'yard_spot') {
        // Moving from yard spot to staging
        movementType = 'yard_spot_to_facility_zone';
        const spot = selectedUnifiedItem.originalData as LocalYardSpot;
        trailerUuid = spot.trailerId || '';
        shipmentId = spot.bol_id || null; // Get shipment_id from yard spot
        driverId = spot.driverId || null; // Get driver_id from yard spot
        variables.yard_spot_uuid = spot.id;
        variables.facility_zone_uuid = spot.zoneId; // Source zone
      } else if (selectedUnifiedItem.type === 'zone_trailer') {
        // Moving from facility zone to staging
        movementType = 'facility_zone_to_facility_zone';
        const zoneTrailer = selectedUnifiedItem.originalData as ZoneTrailer;
        trailerUuid = zoneTrailer.trailer_uuid || '';
        // Get shipment_id from zone trailer if it exists
        shipmentId = (zoneTrailer as any).shipment_id || null;
        // Get driver_id from zone trailer if it exists
        driverId = zoneTrailer.driver_id || null;
        variables.source_facility_zone_uuid = zoneTrailer.zone_id;
      } else {
        throw new Error('Unknown source type for unassigning trailer');
      }
      
      variables.trailer_uuid = trailerUuid;
      variables.movement_type = movementType;
      
      // Always include shipment_id if it exists to preserve shipment relationship
      if (shipmentId) {
        variables.shipment_id = shipmentId;
      }
      
      // Always include driver_id if it exists to preserve driver assignment
      if (driverId) {
        variables.driver_id = driverId;
      }
      
      // Create the movement task mutation
      const createTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $yard_spot_uuid: String,
          $facility_zone_uuid: String,
          $source_facility_zone_uuid: String,
          $destination_facility_zone_uuid: String,
          $shipment_id: String,
          $driver_id: String,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            yard_spot_uuid: $yard_spot_uuid
            facility_zone_uuid: $facility_zone_uuid
            source_facility_zone_uuid: $source_facility_zone_uuid
            destination_facility_zone_uuid: $destination_facility_zone_uuid
            shipment_id: $shipment_id
            driver_id: $driver_id
            force: $force
            auto_complete: $auto_complete
          }) {
            uuid
            task_status {
              status
            }
          }
        }
      `;
      
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createTaskMutation,
          variables
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0]?.message || 'Failed to create unassign task');
      }
      
      console.log('Trailer unassigned successfully:', result.data.create_movement_task);
      
      // Update local state based on source type
      if (selectedUnifiedItem.type === 'yard_spot') {
        // Clear the yard spot
        const spot = selectedUnifiedItem.originalData as LocalYardSpot;
        updateSpotToAvailable(spot.id);
      } else if (selectedUnifiedItem.type === 'zone_trailer') {
        // Remove from zone trailers (it will reappear in staging zone after backend processes it)
        const zoneTrailer = selectedUnifiedItem.originalData as ZoneTrailer;
        setZoneTrailers(prev => 
          prev.filter(trailer => trailer.puid !== zoneTrailer.puid)
        );
      }
      
      // Close the modal
      handleCloseUnifiedModal();
      
      // Show success message
      alert(`Trailer ${selectedUnifiedItem.trailerNumber} has been unassigned and moved to staging.`);
      
      // Reload the page to refresh all data
      window.location.reload();
      
    } catch (error: any) {
      console.error('Error unassigning trailer:', error);
      alert(`Failed to unassign trailer: ${error.message}`);
      setIsUnassigningTrailer(false);
    }
  };

  // Function to save trailer lineage notes
  const handleSaveTrailerNotes = async () => {
    if (!selectedZoneTrailer || !selectedZoneTrailer.trailer_uuid) return;
    
    setSavingTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL mutation to update trailer notes
      const updateTrailerMutation = `
        mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
            trailer_number
            active_lineage {
              uuid
              notes
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            uuid: selectedZoneTrailer.trailer_uuid,
            input: {
              notes: trailerNotesValue
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Update the selected trailer with the new notes immediately for UI feedback
      if (selectedZoneTrailer.active_lineage) {
        selectedZoneTrailer.active_lineage.notes = trailerNotesValue;
      } else {
        selectedZoneTrailer.active_lineage = {
          uuid: result.data.updateTrailer.active_lineage?.uuid || '',
          notes: trailerNotesValue
        };
      }
      
      // Exit edit mode
      setIsEditingTrailerNotes(false);
      
             // Refresh data to ensure consistency
       await refreshData();
       
     } catch (error: any) {
       console.error('Failed to save trailer notes:', error);
       // Note: Error handling would be shown in UI via error state from the hook
     } finally {
       setSavingTrailerNotes(false);
     }
  };

  // Function to save spot trailer lineage notes
  const handleSaveSpotTrailerNotes = async () => {
    // Get trailer UUID from either selectedSpot or selectedUnifiedItem (for zone trailers)
    const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;
    if (!trailerUuid) return;
    
    setSavingSpotTrailerNotes(true);
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL mutation to update trailer notes
      const updateTrailerMutation = `
        mutation UpdateTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
            trailer_number
            active_lineage {
              uuid
              notes
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            uuid: trailerUuid,
            input: {
              notes: spotTrailerNotesValue
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Update the UI state with the new notes immediately for UI feedback
      if (selectedSpot?.active_lineage) {
        selectedSpot.active_lineage.notes = spotTrailerNotesValue;
      } else if (selectedSpot) {
        selectedSpot.active_lineage = {
          uuid: result.data.updateTrailer.active_lineage?.uuid || '',
          notes: spotTrailerNotesValue
        };
      }
      
      // Also update unified item if it exists
      if (selectedUnifiedItem?.active_lineage) {
        selectedUnifiedItem.active_lineage.notes = spotTrailerNotesValue;
      } else if (selectedUnifiedItem) {
        selectedUnifiedItem.active_lineage = {
          uuid: result.data.updateTrailer.active_lineage?.uuid || '',
          notes: spotTrailerNotesValue
        };
      }
      
      // Exit edit mode and clear the notes value
      setIsEditingSpotTrailerNotes(false);
      setSpotTrailerNotesValue('');
      
      // Refresh data to ensure consistency
      await refreshData();
      
    } catch (error: any) {
      console.error('Failed to save spot trailer notes:', error);
      // Note: Error handling would be shown in UI via error state from the hook
    } finally {
      setSavingSpotTrailerNotes(false);
    }
  };

  // Validate shipment reference and get shipment ID
  const validateShipmentReference = async (referenceNumber: string): Promise<string | null> => {
    if (!referenceNumber.trim()) return null;

    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Get trailer UUID from either selectedSpot or selectedUnifiedItem
      const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;

      const shipmentValidationQuery = `
        query GetShipmentIdByPUID {
          shipment_without_trailer_by_reference_number(reference_number: "${referenceNumber.trim()}", facility_id: "${selectedSpot?.facilityId || facilityOfTrailer(trailerUuid)}", trailer_uuid: "${trailerUuid}") {
            id
          }
        }
      `;

      const validationResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: shipmentValidationQuery
        })
      });

      const validationResult = await validationResponse.json();

      if (validationResult.errors) {
        throw new Error(validationResult.errors[0].message);
      }

      const shipmentId = validationResult.data?.shipment_without_trailer_by_reference_number?.id;
      
      if (!shipmentId) {
        throw new Error(`No unassigned shipment found with reference number: ${referenceNumber.trim()}`);
      }

      return shipmentId;
    } catch (err: any) {
      console.error('Error validating shipment reference:', err);
      throw err;
    }
  };

  // Handle update trailer shipment
  const handleUpdateTrailerShipment = async () => {
    // Get trailer UUID from either selectedSpot or selectedUnifiedItem
    const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;
    if (!trailerUuid) return;
    
    try {
      setIsUpdatingShipment(true);
      
      let shipmentId = null;
      
      // Validate shipment reference if provided
      if (newShipmentReference.trim()) {
        try {
          shipmentId = await validateShipmentReference(newShipmentReference);
          setShipmentReferenceError(''); // Clear any previous error
        } catch (validationError: any) {
          setShipmentReferenceError(validationError.message);
          setIsUpdatingShipment(false);
          return;
        }
      } else {
        setShipmentReferenceError(''); // Clear error if no reference number
      }
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const updateTrailerMutation = `
        mutation UpdateTrailer($trailerUuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $trailerUuid, input: $input) {
            uuid
            trailer_number
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: updateTrailerMutation,
          variables: {
            trailerUuid: trailerUuid,
            input: {
              shipmentId: shipmentId
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to show updated shipment
      await refreshData();
      
      // Clear the input and close the modal
      setNewShipmentReference('');
      setShowUnifiedDetailsModal(false);
      setSelectedUnifiedItem(null);
      setSelectedSpot(null);
      
    } catch (error: any) {
      console.error('Failed to update shipment:', error);
      setShipmentReferenceError(error.message || 'Failed to update shipment. Please try again.');
    } finally {
      setIsUpdatingShipment(false);
    }
  };

  // Helper function to determine if remove shipment button should be enabled
  const isRemoveShipmentButtonEnabled = () => {
    const shipmentNumber = selectedSpot?.shipmentNumber || selectedUnifiedItem?.shipmentNumber;
    if (!shipmentNumber) return false;
    
    // Enable button for yard spots - allow removal from yard
    return true;
  };

  // Function to remove shipment relationship
  const handleRemoveShipmentRelationship = async () => {
    // Get trailer UUID from either selectedSpot or selectedUnifiedItem
    const trailerUuid = selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid;
    if (!trailerUuid) return;
    
    try {
      setIsRemovingShipment(true);
      
      // Get auth token from cookie
      const authToken = getAuthToken();

      const removeShipmentMutation = `
        mutation RemoveShipmentFromTrailer($uuid: String!, $input: TrailerUpdateInput!) {
          updateTrailer(uuid: $uuid, input: $input) {
            uuid
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: removeShipmentMutation,
          variables: {
            uuid: trailerUuid,
            input: {
              shipmentId: null
            }
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Close confirmation modal and refresh data
      setShowRemoveShipmentConfirm(false);
      await refreshData();
      
    } catch (error: any) {
      console.error('Failed to remove shipment relationship:', error);
      alert('Failed to remove shipment relationship. Please try again.');
    } finally {
      setIsRemovingShipment(false);
    }
  };

  // Add handler for closing the move to dock modal
  const handleCloseMoveToDockModal = () => {
    setShowMoveToDockModal(false);
    setSelectedDockDoor('');
    setDockFilter('');
    setSelectedShipmentPuid('');
    setAutoCompleteMoveToYardSpot(false);
    setMoveToDockError(null);
  };

    // Update handler for moving trailer to dock using create_movement_task
  const handleMoveTrailerToDock = async () => {
    if (!selectedDockDoor) {
      setMoveToDockError('Dock door is required');
      return;
    }

        if (!selectedSpot) {
      setMoveToDockError('No yard spot selected');
      return;
    }

    // Shipment reference number is now required
    // if (!selectedShipmentPuid || !selectedShipmentPuid.trim()) {
    //   setMoveToDockError('Shipment reference number is required');
    //   return;
    // }

    setMovingToDock(true);
    setMoveToDockError(null);
    
    let shipmentId = null;
    
    // Always validate reference number since it's required
    setValidatingShipment(true);
      
    if (selectedShipmentPuid && selectedShipmentPuid.trim()) {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Validate that the shipment reference number exists
      const shipmentValidationQuery = `
        query GetShipmentIdByPUID {
          shipment_without_trailer_by_reference_number(reference_number: "${selectedShipmentPuid.trim()}", facility_id: "${selectedSpot?.facilityId || facilityOfTrailer(selectedSpot?.trailerId)}", trailer_uuid: "${selectedSpot?.trailerId || ""}") {
            
              id
             
          }
        }
      `;

      const validationResponse = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: shipmentValidationQuery
        })
      });

      const validationResult = await validationResponse.json();
      


      if (validationResult.errors) {
        // Check if the error is "Shipment not found"
        const shipmentNotFoundError = validationResult.errors.find(
          (error: any) => error.message === "Shipment not found" && 
          error.path && error.path.includes("shipment_without_trailer_by_reference_number")
        );
        
        if (shipmentNotFoundError) {
          setMoveToDockError(`No shipment found with reference number "${selectedShipmentPuid}". Please verify the reference number.`);
          setValidatingShipment(false);
          setMovingToDock(false);
          return;
        }
        
        // For other errors, throw them
        throw new Error(validationResult.errors[0].message);
      }

      // Check if shipment was found
      const shipment = validationResult.data?.shipment_without_trailer_by_reference_number;
      if (!shipment || !shipment.id) {
        setMoveToDockError(`No shipment found with reference number "${selectedShipmentPuid}". Please verify the reference number.`);
        setValidatingShipment(false);
        setMovingToDock(false);
        return;
      }

      console.log('Shipment validation successful:', shipment);
      shipmentId = shipment.id;
      setValidatingShipment(false);
    } catch (err) {
      console.error('Error validating shipment:', err);
      setMoveToDockError('Failed to validate reference number. Please try again.');
        setValidatingShipment(false);
        setMovingToDock(false);
        return;
      }
    }
    else{
        const confirmed = window.confirm('Are you sure you want to move the trailer to dock without a shipment reference number?');
        if (!confirmed) {
          setMovingToDock(false);
          setValidatingShipment(false);
          setMoveToDockError('Failed to validate reference number. Please try again.');
          return;
        }
    }
    
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // GraphQL mutation for creating movement task to dock
      const createMovementTaskMutation = `
        mutation CreateMovementTask(
          $trailer_uuid: String!,
          $movement_type: String!,
          $facility_id: ID!,
          $destination_facility_id: ID,
          $warehouse_zone_uuid: String,
          $dock_uuid: String,
          $facility_zone_uuid: String,
          $yard_spot_uuid: String,
          $source_facility_zone_uuid: String,
          $destination_facility_zone_uuid: String,
          $driver_id: String,
          $shipment_id: String,
          $trailer_status_uuid: String,
          $force: Boolean,
          $auto_complete: Boolean
        ) {
          create_movement_task(input: {
            trailer_uuid: $trailer_uuid
            movement_type: $movement_type
            facility_id: $facility_id
            destination_facility_id: $destination_facility_id
            warehouse_zone_uuid: $warehouse_zone_uuid
            dock_uuid: $dock_uuid
            facility_zone_uuid: $facility_zone_uuid
            yard_spot_uuid: $yard_spot_uuid
            source_facility_zone_uuid: $source_facility_zone_uuid
            destination_facility_zone_uuid: $destination_facility_zone_uuid
            driver_id: $driver_id
            shipment_id: $shipment_id
            trailer_status_uuid: $trailer_status_uuid
            force: $force
            auto_complete: $auto_complete
          }) {
            uuid
            data
            task_type {
              uuid
              type
            }
            task_status {
              uuid
              status
            }
            from_location
            to_location
            trailer {
              uuid
              trailer_number
            }
          }
        }
      `;

      // Find the warehouse zone for the selected dock door
      // Support both UUID (from button clicks) and door name (from manual input)
      let selectedDockInfo = moveDestinations.dockDoors.find(dockInfo => dockInfo.dockUuid === selectedDockDoor);
      
      // If not found by UUID, try to find by door name (manual input)
      if (!selectedDockInfo) {
        selectedDockInfo = moveDestinations.dockDoors.find(dockInfo => dockInfo.door === selectedDockDoor);
      }
      
      if (!selectedDockInfo) {
        throw new Error(`Dock door ${selectedDockDoor} not found in active dock doors`);
      }
      
      // Prepare variables for the mutation
      const variables: any = {
        trailer_uuid: selectedSpot.trailerId, 
        movement_type: 'yard_to_warehouse',
        facility_id: selectedSpot.facilityId || facilityOfTrailer(selectedSpot.trailerId),
        dock_uuid: selectedDockInfo.dockUuid, // Use the actual dock UUID, not the dock name
        warehouse_zone_uuid: selectedDockInfo.zoneUuid,
        source_facility_zone_uuid: null,
        destination_facility_zone_uuid: null,
        driver_id: selectedSpot.driverId || undefined,
        shipment_id: shipmentId || undefined, // Use the validated shipment ID if available
        trailer_status_uuid: undefined, // Optional field
        force: false
      };

      // Add yard spot info if this is coming from a yard spot
      if (selectedSpot.id && !selectedSpot.id.startsWith('zone-trailer-')) {
        variables.facility_zone_uuid = selectedSpot.zoneId;
        variables.yard_spot_uuid = selectedSpot.id;
      }
      
      // Add auto_complete if checked
      if (autoCompleteMoveToYardSpot) {
        variables.auto_complete = true;
      }

      console.log('Creating movement task to dock with variables:', variables);

      // Execute the GraphQL mutation
      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: createMovementTaskMutation,
          variables: withCampusFacilities(variables, variables.facility_id, selectedDockInfo.facilityId)
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        console.error('GraphQL errors:', result.errors);
        throw new Error(result.errors[0].message);
      }

      // Check if the movement task was created successfully
      if (!result.data?.create_movement_task?.uuid) {
        throw new Error('Failed to create movement task to dock');
      }

      console.log('Movement task to dock created successfully:', result.data.create_movement_task);
      
      // Lock the spot or zone trailer to show it's in a pending state
      if (selectedSpot) {
        if (selectedSpot.id.startsWith('zone-trailer-')) {
          // It's a zone trailer
          const trailerId = selectedSpot.id.replace('zone-trailer-', '');
          setLockedZoneTrailers(prev => new Set(prev).add(trailerId));
        } else {
          // It's a yard spot
          setLockedSpots(prev => new Set(prev).add(selectedSpot.id));
        }
      }
      
      // Store the task data and show success modal
      setCreatedTask(result.data.create_movement_task);
      setShowSuccessModal(true);
      
      // Update local state after successful API call
      if (selectedSpot && !selectedSpot.id.startsWith('zone-trailer-')) {
        updateSpotToAvailable(selectedSpot.id);
      }
      
      // Close both modals
      handleCloseMoveToDockModal();
      if (showUnifiedDetailsModal) {
        handleCloseUnifiedModal();
      }
      
      // Refresh data after a delay to show the locked state
      setTimeout(() => {
        refreshData();
      }, 2000);
    } catch (err) {
      console.error('Error creating movement task to dock:', err);
      setMoveToDockError('Failed to move trailer to dock. Please try again.');
    } finally {
      setMovingToDock(false);
      setValidatingShipment(false);
    }
  };

  // Function to cancel a task
  const handleCancelTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const cancelTaskMutation = `
        mutation CancelTask($taskUuid: String!) {
          cancel_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: cancelTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      refreshData();
    } catch (error) {
      console.error('Failed to cancel task:', error);
      throw error;
    }
  };

  // Function to complete a task
  const handleCompleteTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeTaskMutation = `
        mutation CompleteTask($taskUuid: String!) {
          complete_task(uuid: $taskUuid) {
            uuid
            task_status {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeTaskMutation,
          variables: { taskUuid: taskId }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      refreshData();
    } catch (error) {
      console.error('Failed to complete task:', error);
      throw error;
    }
  };

  // Function to complete a movement task
  const handleCompleteMovementTask = async (taskId: string) => {
    try {
      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      const completeMovementTaskMutation = `
        mutation CompleteMovementTask($input: CompleteMovementTaskInput!) {
          completeMovementTask(input: $input) {
            uuid
            taskStatus {
              status
            }
          }
        }
      `;

      const response = await fetch(GRAPHQL_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          query: completeMovementTaskMutation,
          variables: { 
            input: { 
              taskUuid: taskId 
            } 
          }
        })
      });

      const result = await response.json();
      
      if (result.errors) {
        throw new Error(result.errors[0].message);
      }

      // Refresh data to update the UI
      refreshData();
    } catch (error) {
      console.error('Failed to complete movement task:', error);
      throw error;
    }
  };

  // Function to fetch and show task details from a lock
  const handleShowTaskFromLock = async (lock: any) => {
    if (!lock.task_uuid) {
      console.error('No task UUID in lock object');
      return;
    }

    try {
      // Get all locks for the same resource to find all related tasks
      const resourceUuid = lock.resource_uuid || lock.resource?.uuid;
      const allResourceLocks = resourceUuid ? activeLocks.filter((resourceLock: any) => {
        const lockUuid = resourceLock.resource_uuid || resourceLock.resource?.uuid;
        return lockUuid === resourceUuid;
      }) : [lock];
      
      // Extract all task UUIDs from the locks
      const taskUuids = allResourceLocks
        .filter(resourceLock => resourceLock.task_uuid)
        .map(resourceLock => resourceLock.task_uuid);

      if (taskUuids.length === 0) {
        console.error('No task UUIDs found in locks');
        return;
      }

      // Get auth token from cookie or localStorage
      const authToken = getAuthToken();

      // Fetch all tasks for the UUIDs
      const taskPromises = taskUuids.map(async (taskUuid) => {
        const taskQuery = `
          query GetTask($taskUuid: String!) {
            task(id: $taskUuid) {
              uuid
              data
              task_type {
                uuid
                type
              }
              task_status {
                uuid
                status
              }
              from_location
              to_location
              trailer {
                uuid
                trailer_number
              }
              created_at
              updated_at
            }
          }
        `;

        const response = await fetch(GRAPHQL_API_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            query: taskQuery,
            variables: { taskUuid }
          })
        });

        const result = await response.json();
        if (result.data?.task) {
          return result.data.task;
        }
        return null;
      });

      const tasks = (await Promise.all(taskPromises)).filter(task => task !== null);

      if (tasks.length === 0) {
        // Fallback to original single task approach
        console.warn('Failed to fetch any tasks, falling back to single task');
        const fallbackTask = {
          uuid: lock.task_uuid,
          task_status: { status: 'ACTIVE' },
          task_type: { type: lock.operation_type || 'Movement Task' },
          from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
          to_location: 'Unknown',
          trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
          created_at: lock.created_at,
          data: {
            reason: lock.reason,
            operationType: lock.operation_type
          }
        };
        setSelectedTaskForDetails(fallbackTask);
        setShowTaskDetailsModal(true);
        return;
      }

      // Prioritize MOVE_TRAILER tasks - show MOVE_TRAILER if it exists, otherwise show first task
      const moveTrailerTask = tasks.find(task => 
        task.task_type?.type === 'MOVE_TRAILER' &&
        task.task_status?.status !== 'COMPLETED' && 
        task.task_status?.status !== 'CANCELLED'
      );
      
      const selectedTask = moveTrailerTask || tasks[0];
      setSelectedTaskForDetails(selectedTask);
      setShowTaskDetailsModal(true);

    } catch (error) {
      console.error('Error fetching task details:', error);
      // Show fallback task details from lock
      const fallbackTask = {
        uuid: lock.task_uuid,
        task_status: { status: 'ACTIVE' },
        task_type: { type: lock.operation_type || 'Movement Task' },
        from_location: `${lock.model_name}: ${lock.resource?.name || lock.resource_uuid}`,
        to_location: 'Unknown',
        trailer: lock.resource?.trailerNumber ? { trailer_number: lock.resource.trailerNumber } : null,
        created_at: lock.created_at,
        data: {
          reason: lock.reason,
          operationType: lock.operation_type
        }
      };
      setSelectedTaskForDetails(fallbackTask);
      setShowTaskDetailsModal(true);
    }
  };

  // Main component render
  return (
    <div className={window.location.search.includes('mode=android') ? 'p-5 w-full' : 'p-5 max-w-7xl mx-auto'}>
      {/* Error handling */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-5" role="alert">
          <p className="font-bold">Error</p>
          <p>{error}</p>
        </div>
      )}
      
      {/* Header - Always show regardless of loading state or yard spots */}
      <div className="bg-white p-5 shadow rounded">
        <div className="flex justify-between items-center mb-2">
          <h1 className="text-2xl font-bold">Yard Spot Management</h1>
          
          <div className="flex flex-col items-end">
            {/* Refresh Button */}
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white flex items-center px-4 py-2 rounded shadow"
              onClick={handleRefreshData}
              disabled={apiLoading || loadingStagingTrailers || loadingZoneTrailers}
            >
              {apiLoading || loadingStagingTrailers || loadingZoneTrailers ? (
                <>
                  <CircularProgress size={16} className="mr-2" color="inherit" />
                  <span>Refreshing...</span>
                </>
              ) : (
                <>
                  <Refresh fontSize="small" className="mr-2" />
                  <span>Refresh Data</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Content based on loading state and data availability */}
      {apiLoading ? (
        <div className="flex justify-center items-center h-64">
          <CircularProgress />
        </div>
      ) : yardSpots.length === 0 ? (
        <div className="flex flex-col gap-5">
          {/* Show facility selector even when no spots */}
          <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow">
            <div className="flex items-center">
              <Select 
                options={facilities.map(f => ({ value: f.id, label: facilityDisplayName(f) }))}
                value={{ value: selectedFacility?.id, label: selectedFacilityLabel }}
                onChange={(option) => {
                  const facility = facilities.find(f => f.id === option?.value);
                  if (facility) setSelectedFacility(facility);
                }}
                className="w-48"
                isSearchable
              />
            </div>
          </div>
          
          <div className="flex flex-col justify-center items-center h-64 bg-white p-5 shadow rounded">
            <div className="text-xl font-semibold text-gray-600 mb-2">No Yard Spots Found</div>
            <p className="text-gray-500">There are currently no yard spots available for the selected facility.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-5">
          
          {/* Tabs */}
          <div className="bg-white shadow">
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              indicatorColor="primary"
              textColor="primary"
            >
              <Tab label="Current Status" />
              <Tab label="Trailer History" />
            </Tabs>
          </div>

          {/* Tab content */}
          {tabValue === 0 && (
            <div>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 items-center bg-white p-3 shadow mb-4">
                <div className="flex items-center">
                  <Select 
                    options={facilities.map(f => ({ value: f.id, label: facilityDisplayName(f) }))}
                    value={{ value: selectedFacility?.id, label: selectedFacilityLabel }}
                    onChange={(option) => {
                      const facility = facilities.find(f => f.id === option?.value);
                      if (facility) setSelectedFacility(facility);
                    }}
                    className="w-48"
                    isSearchable
                  />
                </div>

                {/* Which facilities of the campus to show - only in a multi-facility campus */}
                <CampusFacilityMultiSelect
                  facilities={campusFacilities.map(f => ({ id: String(f.id), name: facilityDisplayName(f) }))}
                  value={selectedCampusFacilityIds}
                  onChange={setSelectedCampusFacilityIds}
                />

                {/* Divider between facility and saved filters */}
                <div className="h-8 w-px bg-gray-300 mx-2"></div>
                
                <div className="flex items-center">
                  <Select
                    options={[
                      { value: null, label: 'All Zones' },
                      ...zones
                        .filter(zone => (zone.baseName ?? zone.name) !== "Staging Area") // Remove Staging Area from dropdown
                        .sort((a, b) => a.name.localeCompare(b.name)) // Sort zones alphabetically
                        .map(zone => {
                          const spotCount = yardSpots.filter(s => s.zoneId === zone.id).length;
                          return {
                            value: zone.id,
                            label: `${zone.name} (${spotCount})`
                          };
                        })
                    ]}
                    value={
                      filterZone 
                        ? { 
                            value: filterZone, 
                            label: (() => {
                              const zone = zones.find(z => z.id === filterZone);
                              const spotCount = yardSpots.filter(s => s.zoneId === filterZone).length;
                              return zone ? `${zone.name} (${spotCount})` : 'Unknown Zone';
                            })()
                          }
                        : { value: null, label: 'All Zones' }
                    }
                    onChange={(option) => {
                      setFilterZone(option?.value || null);
                      // Clear spot batch filter when zone changes
                      setSpotBatchFilter(null);
                      // Save to localStorage when changed
                      try {
                        if (option?.value) {
                          localStorage.setItem(STORAGE_KEYS.ZONE_FILTER, option.value);
                        } else {
                          localStorage.removeItem(STORAGE_KEYS.ZONE_FILTER);
                        }
                        // Clear spot batch when zone changes
                        localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
                      } catch (error) {
                        console.error('Error saving zone filter:', error);
                      }
                    }}
                    className="w-40"
                  />
                </div>
                
                {filterZone && (
                  <div className="flex items-center">
                    <Select
                      options={[
                        { value: null, label: 'All Spots' },
                        ...generateSpotBatches.map(batch => ({
                          value: batch.label,
                          label: batch.label
                        }))
                      ]}
                      value={
                        spotBatchFilter 
                          ? { value: spotBatchFilter, label: spotBatchFilter }
                          : { value: null, label: 'All Spots' }
                      }
                      onChange={(option) => {
                        setSpotBatchFilter(option?.value || null);
                        // Save to localStorage when changed
                        try {
                          if (option?.value) {
                            localStorage.setItem(STORAGE_KEYS.SPOT_BATCH, option.value);
                          } else {
                            localStorage.removeItem(STORAGE_KEYS.SPOT_BATCH);
                          }
                        } catch (error) {
                          console.error('Error saving spot batch filter:', error);
                        }
                      }}
                      className="w-40"
                    />
                  </div>
                )}

                <div className="flex items-center">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={showUnallocatedAssets}
                        onChange={(e) => {
                          setShowUnallocatedAssets(e.target.checked);
                          // Save to localStorage when changed
                          try {
                            localStorage.setItem(STORAGE_KEYS.SHOW_UNALLOCATED, e.target.checked.toString());
                          } catch (error) {
                            console.error('Error saving unallocated assets preference:', error);
                          }
                        }}
                        size="small"
                      />
                    }
                    label="Show Unallocated Assets"
                  />
                </div>

                {/* Divider between filters and search */}
                <div className="h-8 w-px bg-gray-300 mx-2"></div>

                {/* Search Field */}
                <div className="flex items-center">
                  <TextField
                    size="small"
                    placeholder="Search trailers, status..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 200 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: searchTerm && (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearchTerm('')}
                            edge="end"
                          >
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                  />
                </div>

                {/* Filter Tag Buttons */}
                <div className="flex items-center gap-1">
                  <Chip
                    label="EMPTY"
                    size="small"
                    variant={searchHasTag('EMPTY') ? 'filled' : 'outlined'}
                    color={searchHasTag('EMPTY') ? 'warning' : 'default'}
                    onClick={() => toggleSearchTag('EMPTY')}
                    sx={{ cursor: 'pointer', fontWeight: searchHasTag('EMPTY') ? 700 : 400 }}
                  />
                  <Chip
                    label="LOADED"
                    size="small"
                    variant={searchHasTag('LOADED') ? 'filled' : 'outlined'}
                    color={searchHasTag('LOADED') ? 'primary' : 'default'}
                    onClick={() => toggleSearchTag('LOADED')}
                    sx={{ cursor: 'pointer', fontWeight: searchHasTag('LOADED') ? 700 : 400 }}
                  />
                  <Chip
                    label="OOS"
                    size="small"
                    variant={searchHasTag('OOS') ? 'filled' : 'outlined'}
                    color={searchHasTag('OOS') ? 'error' : 'default'}
                    onClick={() => toggleSearchTag('OOS')}
                    sx={{ cursor: 'pointer', fontWeight: searchHasTag('OOS') ? 700 : 400 }}
                  />
                </div>

                <div className="flex-grow"></div>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={saveZoneFilterPreference}
                  className="whitespace-nowrap"
                >
                  Save Filters
                </Button>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearAllFilters}
                  className="whitespace-nowrap"
                  color="secondary"
                >
                  Clear Filters
                </Button>
               
              </div>
              
              {/* Staging Area - only show if staging zone exists in data */}
            
              
              {/* Grouped Yard Spot Display - with fixed height container */}
              <div ref={spotsContainerRef} className="space-y-6" style={{ minHeight: spotsHeight || 'auto' }}>
                {useZoneBasedDisplay ? (
                  // Zone-based trailer display (parking lot style)
                   (
                  // Show all zones with either yard spots or trailers
                  zones
                    .sort((a, b) => a.name.localeCompare(b.name)) // Sort zones alphabetically
                    .map(zone => {
                    const zoneColor = zone.color || '#2196F3'; // Default to blue if no color is defined
                    const zoneSpots = displayedYardSpots.filter(spot => spot.zoneId === zone.id)
                      .sort((a, b) => {
                        const orderDiff = (a.order_number || 0) - (b.order_number || 0);
                        if (orderDiff !== 0) return orderDiff;
                        // Secondary sort by displayNumber when order_number is the same
                        return a.displayNumber.localeCompare(b.displayNumber, undefined, { numeric: true });
                      });
                      if (filterZone && filterZone !== zone.id) {
                        return null;
                      }
                    const zoneTrailers = trailersByZone.find(group => group.zone.id === zone.id)?.trailers.sort((a, b) => {
                      // Sort by trailer_lineage.created_at descending (most recent first)
                      const aCreatedAt = lineageCheckIn(a.active_lineage);
                      const bCreatedAt = lineageCheckIn(b.active_lineage);
                      
                      if (aCreatedAt && bCreatedAt) {
                        return new Date(bCreatedAt).getTime() - new Date(aCreatedAt).getTime();
                      }
                      
                      // If one has created_at and the other doesn't, prioritize the one with created_at
                      if (aCreatedAt && !bCreatedAt) return -1;
                      if (!aCreatedAt && bCreatedAt) return 1;
                      
                      // If neither has created_at, sort by trailer_number as fallback
                      return (a.trailer_number || '').localeCompare(b.trailer_number || '');
                    }) || [];
                    const hasSpots = zoneSpots.length > 0;
                    const hasTrailers = zoneTrailers.length > 0;
                    const isStaging = zone.zone_type === "staging";
                    
                    return (
                      <div key={zone.id} className={`${isStaging ? 'bg-amber-50 border-2 border-amber-300 border-dashed' : 'bg-white border border-gray-200'} p-4 shadow rounded`}>
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex items-center">
                            {isStaging && <div className="w-4 h-4 bg-amber-400 mr-2 rounded-full animate-pulse"></div>}
                            <h3 className={`font-bold text-lg ${isStaging ? 'text-amber-800' : ''}`} style={{ color: isStaging ? undefined : zoneColor }}>
                              {zone.name}
                            </h3>
                            {isStaging && <span className="ml-2 text-xs bg-amber-200 text-amber-800 px-2 py-1 rounded-full">STAGING</span>}
                          </div>
                          
                        </div>
                        
                        {/* Show yard spots if this zone has them */}

  
                        {/* Show zone trailers if this zone has them */}
                        {hasTrailers && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Unlocated Assets for {zone.name} zone</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                              {
                              zoneTrailers.slice(0, MAX_TILES).map(trailer => (
                                                              <Tile
                                key={trailer.trailer_uuid || `${trailer.puid}-${trailer.trailer_number}`}
                                label={trailer.trailer_number}
                                status={trailer.current_status?.toUpperCase() === 'LOADED' ? 'loaded' : trailer.current_status?.toUpperCase() === 'EMPTY' ? 'empty' : 'in-use'}
                                zoneColor={zoneColor}
                                onClick={() => handleShowUnifiedDetails(trailer, 'zone_trailer')}
                                trailerNumber={trailer.trailer_number}
                                carrierName={trailer.carrier_name || undefined}
                                shipmentNumber={trailer.shipment_number || undefined}
                                isZoneTrailer={true}
                                isLocked={mergeLockSets(taskBasedLockedTrailers, lockedZoneTrailers).has(trailer.trailer_uuid || trailer.puid)}
                                onTaskClick={() => {
                                  // Find and show task details for this trailer
                                  const trailerLocks = activeLocks.filter((lock: any) => {
                                    const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                                    return lockUuid === (trailer.trailer_uuid || trailer.puid) && 
                                           (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                                  });
                                  if (trailerLocks.length > 0) {
                                    // Sort locks to prioritize "movement" first
                                    const sortedLocks = [...trailerLocks].sort((a, b) => {
                                      const aNamespace = a.namespace || '';
                                      const bNamespace = b.namespace || '';
                                      if (aNamespace === 'movement' && bNamespace !== 'movement') return -1;
                                      if (bNamespace === 'movement' && aNamespace !== 'movement') return 1;
                                      return aNamespace.localeCompare(bNamespace);
                                    });
                                    handleShowTaskFromLock(sortedLocks[0]);
                                  }
                                }}
                                isTaskBasedLock={taskBasedLockedTrailers.has(trailer.trailer_uuid || trailer.puid)}
                                shipmentDirection={trailer.direction as 'inbound' | 'outbound' | null}
                                locks={activeLocks.filter((lock: any) => {
                                  const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                                  return lockUuid === (trailer.trailer_uuid || trailer.puid) && 
                                         (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                                })}
                                actionButtons={(() => {
                                  // Check if trailer has an active lock
                                  const trailerHasLock = activeLocks.some((lock: any) => {
                                    const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                                    return lockUuid === (trailer.trailer_uuid || trailer.puid) && 
                                           (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                                  });
                                  
                                  const isDisabled = isTrailerOOS(trailer) || trailerHasLock;
                                  
                                  return [
                                    {
                                      label: trailerHasLock ? "Moving" : "To Yard",
                                      onClick: (e) => {
                                        if (isDisabled) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          return;
                                        }
                                        handleMoveZoneTrailerToYardSpot(trailer);
                                      },
                                      icon: <ArrowForward fontSize="inherit" />,
                                      className: `${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} text-white`
                                    },
                                    {
                                      label: isTrailerOOS(trailer) ? "OOS" : trailerHasLock ? "Moving" : "To Dock",
                                      onClick: (e) => {
                                        if (isDisabled) {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          return;
                                        }
                                        handleMoveZoneTrailerToDock(trailer);
                                      },
                                      icon: <ArrowForward fontSize="inherit" />,
                                      className: `${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white`
                                    }
                                  ];
                                })()}
                                tooltip={
                                  <div className="p-1">
                                    <div className="font-bold">{trailer.trailer_number} - {zone.name}</div>
                                    {trailer.carrier_name && <div className="text-xs">SCAC: {trailer.carrier_name}</div>}
                                    {trailer.driver_name && <div className="text-xs">Driver: {trailer.driver_name}</div>}

                                    <div className="text-xs mt-1">
                                      Direction: {formatDirection(trailer.direction) || 'Unknown'}
                                    </div>
                                    <div className="text-xs">
                                      Status: Zone Trailer
                                    </div>
                                  </div>
                                }
                              />
                              ))}
                              
                              {zoneTrailers.length > MAX_TILES && (
                                <div 
                                  className="w-32 h-32 rounded shadow flex items-center justify-center bg-gray-100 border-2 border-gray-300 cursor-pointer"
                                  onClick={() => setFilterZone(zone.id)}
                                >
                                  <span className="text-sm font-medium text-gray-600">+{zoneTrailers.length - 24}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {hasSpots && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 mb-2">Yard Spots</h4>
                            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                              {zoneSpots.slice(0, MAX_TILES).map(spot => {
                                const getSpotStatus = () => {
                                  return spot.status;
                                };



                                // Show move buttons for spots that have trailers (loaded, empty, or in-use)
                                const showMoveButtons = spot.status === 'in-use' || spot.status === 'loaded' || spot.status === 'empty';

                                return (
                                  <Tile
                                    key={spot.id}
                                    label={spot.displayNumber}
                                    status={getSpotStatus()}
                                    zoneColor={zoneColor}
                                    onClick={() => handleShowUnifiedDetails(spot, 'yard_spot')}
                                    spotName={spot.displayNumber}
                                    trailerNumber={spot.trailerNumber || undefined}
                                    carrierName={spot.carrier_name || undefined}
                                    shipmentNumber={spot.shipmentNumber || undefined}
                                    isZoneTrailer={false}
                                    isLocked={mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id)}
                                    onTaskClick={() => {
                                      const locks = getLocksForYardSpot(spot.id);
                                      if (locks.length > 0) {
                                        // Sort locks to prioritize "movement" first
                                        const sortedLocks = [...locks].sort((a, b) => {
                                          const aNamespace = a.namespace || '';
                                          const bNamespace = b.namespace || '';
                                          if (aNamespace === 'movement' && bNamespace !== 'movement') return -1;
                                          if (bNamespace === 'movement' && aNamespace !== 'movement') return 1;
                                          return aNamespace.localeCompare(bNamespace);
                                        });
                                        handleShowTaskFromLock(sortedLocks[0]);
                                      }
                                    }}
                                    isTaskBasedLock={taskBasedLockedSpots.has(spot.id)}
                                    shipmentDirection={(spot.direction as 'inbound' | 'outbound' | null) || null}
                                    locks={getLocksForYardSpot(spot.id)}
                                    notes={spot.notes}
                                    actionButtons={showMoveButtons ? (() => {
                                      // Check if trailer has an active lock
                                      const trailerHasLock = spot.trailerId && activeLocks.some((lock: any) => {
                                        const lockUuid = lock.resource_uuid || lock.resource?.uuid;
                                        return lockUuid === spot.trailerId && 
                                               (lock.model_name === 'Trailer' || lock.resource?.__typename === 'Trailer');
                                      });
                                      
                                      const isDisabled = isTrailerOOS(spot) || trailerHasLock;
                                      
                                      return [
                                        {
                                          label: trailerHasLock ? "Moving" : "To Yard",
                                          onClick: (e) => {
                                            if (isDisabled) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              return;
                                            }
                                            handleMoveSpotToSpot(spot);
                                          },
                                          icon: <ArrowForward fontSize="inherit" />,
                                          className: `${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'} text-white`
                                        },
                                        {
                                          label: isTrailerOOS(spot) ? "OOS" : trailerHasLock ? "Moving" : "To Dock",
                                          onClick: (e) => {
                                            if (isDisabled) {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              return;
                                            }
                                            handleMoveToDockFromTile(spot);
                                          },
                                          icon: <ArrowForward fontSize="inherit" />,
                                          className: `${isDisabled ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white`
                                        }
                                      ];
                                    })() : undefined}
                                    tooltip={
                                      <div className="p-1">
                                        <div className="font-bold">{spot.displayNumber} - {zone.name}</div>
                                        {spot.status === 'in-use' && (
                                          <>
                                            {!isEmptyTrailer(spot) && !isLoadedTrailer(spot) ? (
                                              <>
                                                {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                                {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                                {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                                {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                              </>
                                            ) : isEmptyTrailer(spot) ? (
                                              <>
                                                <div className="text-xs">Empty Trailer</div>
                                                {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                                {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                              </>
                                            ) : (
                                              <>
                                                <div className="text-xs">Loaded Trailer</div>
                                                {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                                {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                                {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                                {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                              </>
                                            )}
                                          </>
                                        )}
                                        <div className="text-xs mt-1">
                                          Status: {isEmptyTrailer(spot) && spot.status === 'in-use' ? 'Empty Trailer' : 
                                                  isLoadedTrailer(spot) && spot.status === 'in-use' ? 'Loaded Trailer' : 
                                                  spot.status.charAt(0).toUpperCase() + spot.status.slice(1)}
                                        </div>
                                      </div>
                                    }
                                  />
                                );
                              })}
                              
                              {zoneSpots.length > MAX_TILES && (
                                <div 
                                  className="w-32 h-32 rounded shadow flex items-center justify-center bg-gray-100 border-2 border-gray-300 cursor-pointer"
                                  onClick={() => setFilterZone(zone.id)}
                                >
                                  <span className="text-sm font-medium text-gray-600">+{zoneSpots.length - 24}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      
                        
                        {/* Show empty zone message if neither spots nor trailers */}
                        {!hasSpots && !hasTrailers && (
                          <div className={`flex items-center justify-center py-8 ${isStaging ? 'text-amber-600 bg-amber-100' : 'text-gray-500 bg-gray-50'} rounded border-2 border-dashed ${isStaging ? 'border-amber-300' : 'border-gray-300'}`}>
                            <LocalParking className="mr-2" />
                            <span>{isStaging ? 'Staging area ready - No trailers currently staged' : 'Empty zone - No spots or trailers assigned'}</span>
                          </div>
                        )}
                      </div>
                    );
                  })
                )
                ) : (
                  // Original spot-based display
                  filterZone ? (
                                      // If filtering by a specific zone, show only that zone's spots
                  <div className="bg-white p-4 shadow rounded">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="font-bold text-lg" style={{ color: zones.find(z => z.id === filterZone)?.color || '#2196F3' }}>
                        {zones.find(z => z.id === filterZone)?.name || 'Unknown Zone'}
                      </h3>
                        <span className="text-sm text-gray-500">
                          {displayedYardSpots.length} spots
                        </span>
                      </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                      {displayedYardSpots.map(spot => {
                        const getSpotStatus = () => {
                          return spot.status;
                        };


                        // Show move buttons for spots that have trailers (loaded, empty, or in-use)
                        const showMoveButtons = spot.status === 'in-use' || spot.status === 'loaded' || spot.status === 'empty';

                        return (
                          <Tile
                            key={spot.id}
                            label={spot.displayNumber}
                            status={getSpotStatus()}
                            zoneColor={zones.find(z => z.id === spot.zoneId)?.color || '#2196F3'}
                            onClick={() => handleShowUnifiedDetails(spot, 'yard_spot')}
                            spotName={spot.displayNumber}
                            trailerNumber={spot.trailerNumber || undefined}
                            carrierName={spot.carrier_name || undefined}
                            shipmentNumber={spot.shipmentNumber || undefined}
                            isZoneTrailer={false}
                            isLocked={mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id)}
                            onTaskClick={() => {
                              const locks = getLocksForYardSpot(spot.id);
                              if (locks.length > 0) {
                                // Sort locks to prioritize "movement" first
                                const sortedLocks = [...locks].sort((a, b) => {
                                  const aNamespace = a.namespace || '';
                                  const bNamespace = b.namespace || '';
                                  if (aNamespace === 'movement' && bNamespace !== 'movement') return -1;
                                  if (bNamespace === 'movement' && aNamespace !== 'movement') return 1;
                                  return aNamespace.localeCompare(bNamespace);
                                });
                                handleShowTaskFromLock(sortedLocks[0]);
                              }
                            }}
                            isTaskBasedLock={taskBasedLockedSpots.has(spot.id)}
                            shipmentDirection={(spot.direction as 'inbound' | 'outbound' | null) || null}
                            locks={getLocksForYardSpot(spot.id)}
                            notes={spot.notes}
                            actionButtons={showMoveButtons ? [
                              {
                                label: "To Yard",
                                onClick: (e) => handleMoveSpotToSpot(spot),
                                icon: <ArrowForward fontSize="inherit" />,
                                className: "bg-green-500 hover:bg-green-600 text-white"
                              },
                              {
                                label: isTrailerOOS(spot) ? "OOS" : "To Dock",
                                onClick: (e) => {
                                  if (isTrailerOOS(spot)) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    return;
                                  }
                                  handleMoveToDockFromTile(spot);
                                },
                                icon: <ArrowForward fontSize="inherit" />,
                                className: `${isTrailerOOS(spot) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white`
                              }
                            ] : undefined}
                            tooltip={
                              <div className="p-1">
                                <div className="font-bold">{spot.displayNumber} - {zones.find(z => z.id === spot.zoneId)?.name}</div>
                                {spot.status === 'in-use' && (
                                  <>
                                    {!isEmptyTrailer(spot) && !isLoadedTrailer(spot) ? (
                                      <>
                                        {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                        {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                        {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                        {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                      </>
                                    ) : isEmptyTrailer(spot) ? (
                                      <>
                                        <div className="text-xs">Empty Trailer</div>
                                        {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                        {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                      </>
                                    ) : (
                                      <>
                                        <div className="text-xs">Loaded Trailer</div>
                                        {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                        {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                        {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                        {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                      </>
                                    )}
                                  </>
                                )}
                                <div className="text-xs mt-1">
                                  Status: {isEmptyTrailer(spot) && spot.status === 'in-use' ? 'Empty Trailer' : 
                                          isLoadedTrailer(spot) && spot.status === 'in-use' ? 'Loaded Trailer' : 
                                          spot.status.charAt(0).toUpperCase() + spot.status.slice(1)}
                                </div>
                              </div>
                            }
                          />
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  // If not filtering, show all zones
                  spotsByZone.map(({ zone, spots }) => (
                    spots.length > 0 && (
                      <div key={zone.id} className="bg-white p-4 shadow rounded">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="font-bold text-lg" style={{ color: zone.color }}>
                            {zone.name}
                          </h3>
                         
                        </div>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2">
                          {spots.slice(0, MAX_TILES).map(spot => {
                            const getSpotStatus = () => {
                              return spot.status;
                            };

                            // Show move buttons for spots that have trailers (loaded, empty, or in-use)
                            const showMoveButtons = spot.status === 'in-use' || spot.status === 'loaded' || spot.status === 'empty';

                            return (
                                                            <Tile
                                key={spot.id}
                                label={spot.displayNumber}
                                status={getSpotStatus()}
                                zoneColor={zone.color || '#2196F3'}
                                onClick={() => handleShowUnifiedDetails(spot, 'yard_spot')}
                                spotName={spot.displayNumber}
                                trailerNumber={spot.trailerNumber || undefined}
                                carrierName={spot.carrier_name || undefined}
                                shipmentNumber={spot.shipmentNumber || undefined}
                                isZoneTrailer={false}
                                isLocked={mergeLockSets(taskBasedLockedSpots, lockedSpots).has(spot.id)}
                                onTaskClick={() => {
                                  const locks = getLocksForYardSpot(spot.id);
                                  if (locks.length > 0) {
                                    // Sort locks to prioritize "movement" first
                                    const sortedLocks = [...locks].sort((a, b) => {
                                      const aNamespace = a.namespace || '';
                                      const bNamespace = b.namespace || '';
                                      if (aNamespace === 'movement' && bNamespace !== 'movement') return -1;
                                      if (bNamespace === 'movement' && aNamespace !== 'movement') return 1;
                                      return aNamespace.localeCompare(bNamespace);
                                    });
                                    handleShowTaskFromLock(sortedLocks[0]);
                                  }
                                }}
                                isTaskBasedLock={taskBasedLockedSpots.has(spot.id)}
                                shipmentDirection={(spot.direction as 'inbound' | 'outbound' | null) || null}
                                locks={getLocksForYardSpot(spot.id)}
                                actionButtons={showMoveButtons ? [
                                  {
                                    label: "To Yard",
                                    onClick: (e) => handleMoveSpotToSpot(spot),
                                    icon: <ArrowForward fontSize="inherit" />,
                                    className: "bg-green-500 hover:bg-green-600 text-white"
                                  },
                                  {
                                    label: isTrailerOOS(spot) ? "OOS" : "To Dock",
                                    onClick: (e) => {
                                      if (isTrailerOOS(spot)) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        return;
                                      }
                                      handleMoveToDockFromTile(spot);
                                    },
                                    icon: <ArrowForward fontSize="inherit" />,
                                    className: `${isTrailerOOS(spot) ? 'bg-gray-400 cursor-not-allowed' : 'bg-blue-500 hover:bg-blue-600'} text-white`
                                  },
                                  {
                                    label: "To Zone",
                                    onClick: (e) => {
                                      // Convert spot to StagingTrailer and open modal with facility zone destination
                                      const stagingTrailer: StagingTrailer = {
                                        puid: spot.shipmentNumber || spot.id,
                                        driver_id: spot.driverId || '',
                                        driver_name: spot.driverName || '',
                                        driver_phone_number: '',
                                        truck_number: '',
                                        trailer_number: spot.trailerNumber || '',
                                        direction: spot.direction || '',
                                        carrier_name: spot.carrier_name || '',
                                        trailer_uuid: spot.trailerId || spot.id,
                                        current_status: spot.status === 'loaded' ? 'LOADED' : undefined
                                      };
                                      
                                      setSelectedStagingTrailer(stagingTrailer);
                                      setSelectedYardSpotForMove('');
                                      setSelectedYardSpotUuid('');
                                      setSelectedFacilityZone('');
                                      setSelectedDestinationType('facility_zone');
                                      setAssignYardSpotError(null);
                                      setSelectedShipmentPuid(spot.shipmentNumber || '');
                                      setShowMoveToYardSpotModal(true);
                                    },
                                    icon: <ArrowForward fontSize="inherit" />,
                                    className: "bg-purple-500 hover:bg-purple-600 text-white"
                                  }
                                ] : undefined}
                                tooltip={
                                  <div className="p-1">
                                    <div className="font-bold">{spot.displayNumber} - {zone.name}</div>
                                    {spot.status === 'in-use' && (
                                      <>
                                        {!isEmptyTrailer(spot) && !isLoadedTrailer(spot) ? (
                                          <>
                                            {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                            {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                            {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                            {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                          </>
                                        ) : isEmptyTrailer(spot) ? (
                                          <>
                                            <div className="text-xs">Empty Trailer</div>
                                            {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                            {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                          </>
                                        ) : (
                                          <>
                                            <div className="text-xs">Loaded Trailer</div>
                                            {spot.trailerNumber && <div className="text-xs">Trailer: {spot.trailerNumber}</div>}
                                            {spot.carrier_name && <div className="text-xs">SCAC: {spot.carrier_name}</div>}
                                            {spot.shipmentNumber && <div className="text-xs">Shipment: {spot.shipmentNumber}</div>}
                                            {spot.driverName && <div className="text-xs">Driver: {spot.driverName}</div>}
                                          </>
                                        )}
                                      </>
                                    )}
                                    <div className="text-xs mt-1">
                                      Status: {isEmptyTrailer(spot) && spot.status === 'in-use' ? 'Empty Trailer' : 
                                              isLoadedTrailer(spot) && spot.status === 'in-use' ? 'Loaded Trailer' : 
                                              spot.status.charAt(0).toUpperCase() + spot.status.slice(1)}
                                    </div>
                                  </div>
                                }
                              />
                            );
                          })}
                          
                          {spots.length > 24 && (
                            <div 
                              className="w-32 h-32 rounded shadow flex items-center justify-center bg-gray-100 border-2 border-gray-300 cursor-pointer"
                              onClick={() => setFilterZone(zone.id)}
                            >
                              <span className="text-sm font-medium text-gray-600">+{spots.length - 24} more</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )
                                      ))
                  )
                )}
              
              {/* Status Summary - moved to end */}
              <div className="flex flex-wrap gap-2 bg-white p-3 shadow mt-6">
                <span className="font-medium mr-2">Status Summary:</span>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 w-full mt-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-blue-100 border-blue-400 mr-2"></div>
                      <span className="text-sm">Total Trailers</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {zoneTrailers.length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-green-100 border-green-400 mr-2"></div>
                      <span className="text-sm">Available</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {yardSpots.filter(spot => spot.status === 'available').length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-blue-100 border-blue-400 mr-2"></div>
                      <span className="text-sm">Loaded Trailer</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {yardSpots.filter(spot =>
                        spot.status === 'loaded').length +
                       zoneTrailers.filter(trailer =>
                        trailer.current_status?.toUpperCase() === 'LOADED').length}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-6 h-6 rounded border-2 bg-yellow-200 border-yellow-400 mr-2"></div>
                      <span className="text-sm">Empty Trailer</span>
                    </div>
                    <span className="text-xs bg-gray-100 px-2 py-1 rounded-full">
                      {yardSpots.filter(spot =>
                        spot.status === 'empty').length +
                       zoneTrailers.filter(trailer =>
                        trailer.current_status?.toUpperCase() === 'EMPTY').length}
                    </span>
                  </div>
                </div>
              </div>
              </div>
            </div>
          )}

          {/* Trailer History Tab */}
          {tabValue === 1 && (
            <TrailerHistoryTab />
          )}
        </div>
      )}



      {/* Zone Management Modal */}
      {showZoneManageModal && (
        <Modal
          open={showZoneManageModal}
          onClose={handleZoneManageClose}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[500px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleZoneManageClose}
              />
              <h1 className="text-xl font-bold mb-5">Manage Yard Zones</h1>
              
              <div className="mb-5">
                <h2 className="text-lg font-semibold mb-3">Current Zones</h2>
                <div className="space-y-2">
                  {zones.map(zone => (
                    <div 
                      key={zone.id} 
                      className="flex items-center justify-between p-3 border rounded hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        <div 
                          className="w-4 h-4 mr-2 rounded-full"
                          style={{ backgroundColor: zone.color }}
                        ></div>
                        <span className="font-medium">{zone.name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {yardSpots.filter(spot => spot.zoneId === zone.id).length} spots
                        </span>
                        <IconButton size="small" onClick={() => handleShowZoneEdit(zone)}>
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => handleDeleteZone(zone.id)}
                          disabled={yardSpots.filter(spot => spot.zoneId === zone.id).length > 0}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="mb-5 border-t pt-5">
                <h2 className="text-lg font-semibold mb-3">Add New Zone</h2>
                <div className="flex gap-3 mb-3">
                  <TextField
                    label="Zone Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    value={newZoneName}
                    onChange={(e) => setNewZoneName(e.target.value)}
                  />
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={newZoneColor}
                      onChange={(e) => setNewZoneColor(e.target.value)}
                      className="w-10 h-10 rounded cursor-pointer"
                    />
                  </div>
                </div>
                <Button 
                  variant="contained" 
                  color="primary"
                  fullWidth
                  onClick={handleAddNewZone}
                  disabled={!newZoneName.trim()}
                >
                  Add Zone
                </Button>
              </div>
              
              <div className="flex justify-end pt-3 border-t">
                <Button onClick={handleZoneManageClose}>Close</Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Zone Edit Modal */}
      {showZoneEditModal && editingZone && (
        <Modal
          open={showZoneEditModal}
          onClose={handleZoneEditClose}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-96 bg-white border p-5 relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleZoneEditClose}
              />
              <h1 className="text-xl font-bold mb-5">Edit Zone</h1>
              
              {editingZone && (
                <div className="space-y-4 mb-5">
                  <TextField
                    label="Zone Name"
                    variant="outlined"
                    fullWidth
                    value={editingZone.name}
                    onChange={(e) => setEditingZone({ ...editingZone, name: e.target.value })}
                  />
                  
                  <div className="flex items-center gap-3">
                    <span>Zone Color:</span>
                    <input
                      type="color"
                      value={editingZone.color}
                      onChange={(e) => setEditingZone({ ...editingZone, color: e.target.value })}
                      className="w-12 h-12 rounded cursor-pointer"
                    />
                  </div>
                </div>
              )}
              
              <div className="flex justify-between pt-3 border-t">
                <Button onClick={handleZoneEditClose}>Cancel</Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleSaveZone}
                  disabled={editingZone ? !editingZone.name.trim() : true}
                >
                  Save Changes
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}



      {/* Add Scheduled Time Modal */}
     
      {/* Move to Dock Modal */}
      {showMoveToDockModal && selectedSpot && (
        <Modal
          open={showMoveToDockModal}
          onClose={handleCloseMoveToDockModal}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleCloseMoveToDockModal}
              />
              <h1 className="text-xl font-bold mb-4">Move Trailer to Dock Door</h1>
              
              {/* Trailer details */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <span className="font-semibold">Spot:</span> {selectedSpot.displayNumber}
                  </div>
                  {selectedSpot.trailerNumber && (
                    <div className="col-span-2">
                      <span className="font-semibold">Trailer:</span> {selectedSpot.trailerNumber}
                    </div>
                  )}
                  {selectedSpot.carrier_name && (
                    <div className="col-span-2">
                      <span className="font-semibold">SCAC:</span> {selectedSpot.carrier_name}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show error if any */}
              {moveToDockError && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {moveToDockError}
                </div>
              )}
              
              {/* Shipment Reference Number field */}
              <div className="mb-4">
                <h2 className="font-semibold mb-2">Shipment Reference Number *</h2>
                <TextField
                  label="Enter Reference Number *"
                  variant="outlined"
                  fullWidth
                  
                  value={selectedShipmentPuid}
                  onChange={(e) => setSelectedShipmentPuid(e.target.value)}
                  helperText={isLoadedTrailer(selectedSpot) ? "Reference number is read-only for loaded trailers" : "Shipment reference number is required (e.g. REF12345)"}
                  disabled={validatingShipment || movingToDock || isLoadedTrailer(selectedSpot)}
                  error={!selectedShipmentPuid && moveToDockError?.includes('required')}
                />
              </div>
              
              {/* Auto-complete checkbox */}
              <div className="mb-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoCompleteMoveToYardSpot}
                      onChange={(e) => setAutoCompleteMoveToYardSpot(e.target.checked)}
                      disabled={movingToDock || validatingShipment}
                    />
                  }
                  label="Auto-complete task (task will be marked as completed immediately)"
                />
              </div>
              
              {/* Dock door selection */}
              <div className="mb-4">
                <h2 className="font-semibold mb-2">Select Dock Door</h2>
                
                {/* Loading state */}
                {loadingDockDoors || moveDestinations.loading ? (
                  <div className="flex justify-center items-center py-2">
                    <CircularProgress size={24} />
                    <span className="ml-2 text-sm text-gray-600">Loading available dock doors...</span>
                  </div>
                ) : (
                  <>
                <DestinationFacilityFilter
                  facilities={moveDestinations.dockFacilities}
                  value={moveDestinations.facilityFilter}
                  onChange={value => {
                    moveDestinations.setFacilityFilter(value);
                    setSelectedDockDoor('');
                    setDockFilter('');
                  }}
                  disabled={movingToDock || validatingShipment}
                />
                <TextField
                  label="Filter Dock Doors"
                  variant="outlined"
                  fullWidth
                  value={dockFilter}
                  onChange={(e) => {
                    const filterValue = e.target.value;
                    setDockFilter(filterValue);
                    
                    // Auto-selection logic
                    if (filterValue.trim()) {
                      // Find dock doors that match the filter
                      const matchingDocks = moveDestinations.dockDoors.filter(dockInfo => 
                        dockInfo.door.toLowerCase().includes(filterValue.toLowerCase())
                      );
                      
                      // Check for exact match
                      const exactMatch = matchingDocks.find(dockInfo => 
                        dockInfo.door.toLowerCase() === filterValue.toLowerCase()
                      );
                      
                      // Auto-select if there's exactly 1 result and it's an exact match
                      if (matchingDocks.length === 1 && exactMatch) {
                        setSelectedDockDoor(exactMatch.dockUuid);
                      } else {
                        // Clear selection if multiple results or no exact match
                        setSelectedDockDoor('');
                      }
                    } else {
                      // Clear selection when filter is empty
                      setSelectedDockDoor('');
                    }
                  }}
                  helperText="Type to filter dock doors below (e.g. D10)"
                  placeholder="Type dock door name to filter..."
                />
                    
                    {/* Only show available dock doors */}
                    {moveDestinations.dockDoors.length > 0 && (
                      <div className="mt-3">
                        <div className="text-sm text-gray-600 mb-1">Available Docks:</div>
                        <div className="flex flex-wrap gap-2">
                          {(() => {
                            // Filter dock doors based on dock filter
                            const filteredDockDoors = moveDestinations.dockDoors.filter(dockInfo => {
                              const matchesFilter = !dockFilter.trim() || dockInfo.door.toLowerCase().includes(dockFilter.toLowerCase());
                              return matchesFilter;
                            });
                            
                            if (filteredDockDoors.length === 0) {
                              return (
                                <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                                  {dockFilter.trim() 
                                    ? `No dock doors found matching "${dockFilter}". Try clearing the filter.`
                                    : `No dock doors available.`
                                  }
                                </div>
                              );
                            }
                            
                            return filteredDockDoors.map(dockInfo => (
                              <button
                                key={dockInfo.dockUuid}
                                className={`px-2 py-1 border rounded text-sm ${
                                  selectedDockDoor === dockInfo.dockUuid 
                                    ? 'bg-blue-100 border-blue-500' 
                                    : dockInfo.hasMoveFromTask 
                                      ? 'bg-gray-300 hover:bg-gray-400' 
                                      : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                                onClick={() => {
                                  setSelectedDockDoor(dockInfo.dockUuid);
                                  setDockFilter(dockInfo.door);
                                }}
                                title={dockInfo.hasMoveFromTask ? 'Door has an active move-from task' : undefined}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{dockInfo.door}</span>
                                  <span className="text-xs text-gray-500">{dockInfo.zoneName}</span>
                                </div>
                              </button>
                            ));
                          })()}
                        </div>
                      </div>
                    )}
                    
                    {/* No available dock doors message */}
                    {moveDestinations.dockDoors.length === 0 && !loadingDockDoors && !moveDestinations.loading && (
                      <div className="mt-3 text-amber-600 bg-amber-50 p-2 border border-amber-200 rounded text-sm">
                        No active dock doors are currently available. You can still manually enter a dock door number.
                      </div>
                    )}
                  </>
                )}
              </div>
              
              <div className="flex justify-between pt-3 border-t">
                <Button 
                  onClick={handleCloseMoveToDockModal}
                >
                  Cancel
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleMoveTrailerToDock}
                  disabled={!selectedDockDoor.trim() || movingToDock || validatingShipment}
                >
                  {validatingShipment ? (
                    <>
                      <CircularProgress size={16} className="mr-2" />
                      Validating...
                    </>
                  ) : movingToDock ? (
                    <>
                      <CircularProgress size={16} className="mr-2" />
                      Moving...
                    </>
                  ) : (
                    'Move to Dock'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Unified Details Modal */}
      {showUnifiedDetailsModal && selectedUnifiedItem && (
        <Modal
          open={showUnifiedDetailsModal}
          onClose={handleCloseUnifiedModal}
          className="flex justify-center items-center"
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[600px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleCloseUnifiedModal}
              />
              <h1 className="text-xl font-bold mb-5">{selectedUnifiedItem.displayName} Details</h1>
              
              {/* Action buttons for trailers */}
              {(selectedUnifiedItem.trailerNumber || selectedUnifiedItem.type === 'zone_trailer') && (
                <div className="flex gap-2 mb-4">
                  <button 
                    className="bg-green-500 hover:bg-green-600 text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1"
                    onClick={() => {
                      handleCloseUnifiedModal();
                      if (selectedUnifiedItem.type === 'yard_spot') {
                        handleMoveSpotToSpot(selectedUnifiedItem.originalData as LocalYardSpot);
                      } else {
                        handleMoveZoneTrailerToYardSpot(selectedUnifiedItem.originalData as ZoneTrailer);
                      }
                    }}
                  >
                    <ArrowForward fontSize="small" className="mr-2" />
                    <span>{selectedUnifiedItem.type === 'yard_spot' ? 'Move to Spot' : 'Move to Yard Spot'}</span>
                  </button>
                  <button 
                    className={`${
                      (selectedUnifiedItem.type === 'yard_spot' && isTrailerOOS(selectedUnifiedItem.originalData as LocalYardSpot)) ||
                      (selectedUnifiedItem.type === 'zone_trailer' && isTrailerOOS(selectedUnifiedItem.originalData as ZoneTrailer))
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-500 hover:bg-blue-600'
                    } text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1`}
                    disabled={
                      (selectedUnifiedItem.type === 'yard_spot' && isTrailerOOS(selectedUnifiedItem.originalData as LocalYardSpot)) ||
                      (selectedUnifiedItem.type === 'zone_trailer' && isTrailerOOS(selectedUnifiedItem.originalData as ZoneTrailer))
                    }
                    onClick={() => {
                      handleCloseUnifiedModal();
                      if (selectedUnifiedItem.type === 'yard_spot') {
                        // Pass the spot: closing the details modal clears selectedSpot.
                        handleMoveToDockFromTile(selectedUnifiedItem.originalData as LocalYardSpot);
                      } else {
                        handleMoveZoneTrailerToDock(selectedUnifiedItem.originalData as ZoneTrailer);
                      }
                    }}
                    title={
                      (selectedUnifiedItem.type === 'yard_spot' && isTrailerOOS(selectedUnifiedItem.originalData as LocalYardSpot)) ||
                      (selectedUnifiedItem.type === 'zone_trailer' && isTrailerOOS(selectedUnifiedItem.originalData as ZoneTrailer))
                        ? 'Trailer is out of service'
                        : 'Move to Dock'
                    }
                  >
                    <ArrowForward fontSize="small" className="mr-2" />
                    <span>Move to Dock</span>
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4">
                {/* Driver Information */}
                {(selectedUnifiedItem.driverName || selectedUnifiedItem.driverPhone) && (
                  <div className="p-3 border rounded bg-blue-50">
                    <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                      <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                      </svg>
                      Driver Information
                    </h3>
                    {selectedUnifiedItem.driverName && (
                      <p className="text-sm text-gray-600 mb-1">
                        Name: {selectedUnifiedItem.driverName}
                      </p>
                    )}
                    {selectedUnifiedItem.driverPhone && (
                      <p className="text-sm text-gray-600">
                        Phone: {selectedUnifiedItem.driverPhone}
                      </p>
                    )}
                  </div>
                )}

                {/* Trailer Information */}
                <div className="p-3 border rounded bg-green-50">
                  <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"/>
                      <path d="M3 4a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 14.846 4.632 16.5 6.414 16.5H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 6H6.28l-.31-1.243A1 1 0 005 4H3z"/>
                    </svg>
                    Trailer Information
                  </h3>
                  
                  {/* Show add trailer form if spot is empty and it's a yard spot */}
                  {!selectedUnifiedItem.trailerNumber && selectedUnifiedItem.type === 'yard_spot' && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">No trailer assigned to this spot</p>
                      <p className="text-xs text-blue-600 mb-3">
                        Search for an existing trailer or enter a new one
                      </p>
                      
                      <div className="space-y-2">
                        {/* Autocomplete to search existing trailers */}
                        <Autocomplete
                          key={selectedUnifiedItem?.displayName || 'autocomplete-key'}
                          options={getAllExistingTrailers()}
                          getOptionLabel={(option) => `${option.scac} ${option.trailerNumber}`}
                          getOptionDisabled={(option) => option.isAtDock === true}
                          inputValue={trailerSearchInput}
                          onInputChange={(event, newInputValue) => setTrailerSearchInput(newInputValue)}
                          filterOptions={(options, { inputValue }) => {
                            const searchValue = inputValue.toLowerCase().trim();
                            if (!searchValue) return options;

                            return options.filter(option => {
                              const scac = (option.scac || '').toLowerCase();
                              const trailerNumber = (option.trailerNumber || '').toLowerCase();

                              // Must have a trailer number to match
                              if (!trailerNumber) return false;

                              // Match only on trailer number substring
                              return trailerNumber.includes(searchValue);
                            });
                          }}
                          blurOnSelect
                          clearOnBlur
                          renderOption={(props, option) => (
                            <li
                              {...props}
                              key={option.trailerUuid}
                              style={{
                                ...props.style,
                                opacity: option.isAtDock ? 0.5 : 1,
                                cursor: option.isAtDock ? 'not-allowed' : 'pointer',
                                backgroundColor: option.isAtDock ? '#f5f5f5' : props.style?.backgroundColor
                              }}
                            >
                              <div style={{ fontSize: '12px', width: '100%' }}>
                                <div style={{
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center'
                                }}>
                                  <span>{option.scac} {option.trailerNumber}</span>
                                  {option.isAtDock && (
                                    <span style={{
                                      fontSize: '9px',
                                      color: '#f44336',
                                      fontWeight: 'normal',
                                      marginLeft: '8px'
                                    }}>
                                      AT DOCK
                                    </span>
                                  )}
                                </div>
                                <div style={{ fontSize: '10px', color: '#666' }}>
                                  {option.location}
                                  {option.isAtDock && ' (Cannot be moved from here)'}
                                </div>
                              </div>
                            </li>
                          )}
                          value={null}
                          onChange={(event, newValue) => {
                            if (newValue && !newValue.isAtDock) {
                              // User selected an existing trailer - populate the fields including shipment number
                              setAddTrailerScac(newValue.scac.toUpperCase());
                              setAddTrailerNumber(newValue.trailerNumber.toUpperCase());
                              setAddTrailerShipmentNumber(newValue.shipmentNumber || '');
                            }
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search Existing Trailers"
                              variant="outlined"
                              size="small"
                              placeholder="Start typing to search..."
                              inputProps={{
                                ...params.inputProps,
                                style: { fontSize: '12px' }
                              }}
                              InputLabelProps={{ style: { fontSize: '12px' } }}
                            />
                          )}
                          disabled={isAddingTrailer}
                          sx={{
                            '& .MuiAutocomplete-input': { fontSize: '12px' },
                            '& .MuiInputBase-root': { fontSize: '12px' }
                          }}
                        />

                        {/* Separate SCAC field */}
                        <TextField
                          label="SCAC"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={addTrailerScac}
                          onChange={(e) => setAddTrailerScac(e.target.value.toUpperCase())}
                          disabled={isAddingTrailer}
                          placeholder="e.g., ABCD"
                          inputProps={{ style: { fontSize: '12px' } }}
                          InputLabelProps={{ style: { fontSize: '12px' } }}
                        />

                        {/* Separate Trailer Number field */}
                        <TextField
                          label="Trailer Number"
                          variant="outlined"
                          size="small"
                          fullWidth
                          value={addTrailerNumber}
                          onChange={(e) => setAddTrailerNumber(e.target.value.toUpperCase())}
                          disabled={isAddingTrailer}
                          placeholder="e.g., 12345"
                          inputProps={{ style: { fontSize: '12px' } }}
                          InputLabelProps={{ style: { fontSize: '12px' } }}
                        />
                        
                        <FormControl variant="outlined" size="small" fullWidth>
                          <InputLabel style={{ fontSize: '12px' }}>Status</InputLabel>
                          <MuiSelect
                            value={addTrailerStatus}
                            onChange={(e) => setAddTrailerStatus(e.target.value as 'EMPTY' | 'LOADED')}
                            label="Status"
                            disabled={isAddingTrailer}
                            style={{ fontSize: '12px' }}
                          >
                            <MenuItem value="EMPTY" style={{ fontSize: '12px' }}>Empty</MenuItem>
                            <MenuItem value="LOADED" style={{ fontSize: '12px' }}>Loaded</MenuItem>
                          </MuiSelect>
                        </FormControl>
                        
                        {addTrailerError && (
                          <div className="space-y-2">
                            <p className="text-xs text-red-600">{addTrailerError}</p>
                            {showCreateAnywayButton && (
                              <button
                                className="w-full text-xs py-1.5 px-3 rounded flex items-center justify-center font-medium bg-orange-500 hover:bg-orange-600 text-white"
                                onClick={handleCreateAnyway}
                                disabled={isAddingTrailer}
                              >
                                Create Anyway
                              </button>
                            )}
                          </div>
                        )}
                        
                        <button
                          className={`w-full text-white text-xs py-1.5 px-3 rounded flex items-center justify-center font-medium ${
                            isAddingTrailer 
                              ? 'bg-gray-400 cursor-not-allowed' 
                              : 'bg-green-500 hover:bg-green-600'
                          }`}
                          onClick={() => handleAddTrailerToSpot()}
                          disabled={isAddingTrailer}
                        >
                          {isAddingTrailer ? (
                            <>
                              <CircularProgress size={12} className="mr-1" style={{color: 'white'}} />
                              Adding Trailer...
                            </>
                          ) : (
                            <>
                              <Add fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                              Add Trailer to Spot
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {selectedUnifiedItem.trailerNumber && (
                    <p className="text-sm text-gray-600 mb-1">
                      Number: {selectedUnifiedItem.trailerNumber}
                    </p>
                  )}
                  {selectedUnifiedItem.carrierName && (
                    <p className="text-sm text-gray-600 mb-1">
                      SCAC: {selectedUnifiedItem.carrierName}
                    </p>
                  )}
                  {selectedUnifiedItem.status && (
                    <p className="text-sm text-gray-600 mb-2">
                      Status: {selectedUnifiedItem.status}
                    </p>
                  )}
                  {lineageCheckIn(selectedUnifiedItem.active_lineage) && (
                    <p className="text-sm text-gray-600 mb-2">
                      Check-in: {new Date(lineageCheckIn(selectedUnifiedItem.active_lineage)!).toLocaleString()}
                    </p>
                  )}
                  
                  {/* Unassign Trailer Button */}
                  {selectedUnifiedItem.trailerNumber && (
                    <button
                      className={`mt-2 w-full text-white text-xs py-1.5 px-3 rounded flex items-center justify-center font-medium ${
                        isUnassigningTrailer 
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-orange-500 hover:bg-orange-600'
                      }`}
                      onClick={handleUnassignTrailer}
                      disabled={isUnassigningTrailer}
                    >
                      {isUnassigningTrailer ? (
                        <>
                          <CircularProgress size={12} className="mr-1" style={{color: 'white'}} />
                          Unassigning...
                        </>
                      ) : (
                        <>
                          <ArrowForward fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                          Unassign Trailer
                        </>
                      )}
                    </button>
                  )}
                  
                  {/* Trailer Notes Section */}
                  {selectedUnifiedItem.active_lineage && (
                    <div className="border-t pt-2 mt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-gray-700">Notes</span>
                        {!isEditingSpotTrailerNotes && (
                          <button
                            className="text-sky-600 text-xs hover:underline flex items-center"
                            onClick={() => setIsEditingSpotTrailerNotes(true)}
                          >
                            <Edit fontSize="small" className="mr-1" style={{fontSize: '12px'}} />
                            {selectedUnifiedItem.active_lineage.notes ? 'Edit' : 'Add'}
                          </button>
                        )}
                      </div>
                      
                      {isEditingSpotTrailerNotes ? (
                        <div>
                          <textarea
                            value={spotTrailerNotesValue}
                            onChange={(e) => setSpotTrailerNotesValue(e.target.value)}
                            placeholder="Enter notes about this trailer..."
                            className="w-full p-2 border rounded h-16 mb-2 text-xs"
                            disabled={savingSpotTrailerNotes}
                          />
                          <div className="flex justify-end gap-2">
                            <button
                              className="text-gray-600 hover:text-gray-800 text-xs"
                              onClick={() => {
                                setSpotTrailerNotesValue(selectedUnifiedItem.active_lineage?.notes || '');
                                setIsEditingSpotTrailerNotes(false);
                              }}
                              disabled={savingSpotTrailerNotes}
                            >
                              Cancel
                            </button>
                            <button
                              className="text-green-600 hover:text-green-800 text-xs flex items-center"
                              onClick={handleSaveSpotTrailerNotes}
                              disabled={savingSpotTrailerNotes}
                            >
                              {savingSpotTrailerNotes ? (
                                <>
                                  <div className="animate-spin h-2 w-2 border border-current border-t-transparent rounded-full mr-1"></div>
                                  Saving...
                                </>
                              ) : (
                                'Save'
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-2 border rounded bg-white">
                          {selectedUnifiedItem.active_lineage.notes ? (
                            <p className="text-xs text-gray-600">{selectedUnifiedItem.active_lineage.notes}</p>
                          ) : (
                            <p className="text-xs text-gray-500 italic">No notes added</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Shipment Information */}
                <div className="p-3 border rounded bg-yellow-50">
                  <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12z" clipRule="evenodd" />
                    </svg>
                    Shipment Information
                  </h3>
                  
                  {/* Current shipment info */}
                  {selectedUnifiedItem.shipmentNumber && (
                    <p className="text-sm text-gray-600 mb-2">
                      Current Reference: {selectedUnifiedItem.shipmentNumber}
                    </p>
                  )}
                  {selectedUnifiedItem.direction && (
                    <p className="text-sm text-gray-600 mb-2">
                      Direction: {formatDirection(selectedUnifiedItem.direction)}
                    </p>
                  )}

                  {/* Editable shipment reference section */}
                  {(selectedSpot?.trailerId || selectedUnifiedItem?.trailerUuid) && (
                    <div className="mt-3 pt-3 border-t border-yellow-200">
                      <p className="text-xs text-gray-600 mb-2">
                        Update Shipment Reference:
                      </p>
                      {shipmentReferenceError && (
                        <div className="mb-2 p-2 bg-red-100 rounded">
                          <p className="text-xs text-red-700">
                            {shipmentReferenceError}
                          </p>
                        </div>
                      )}
                      <div className="flex gap-2">
                        <TextField
                          fullWidth
                          size="small"
                          value={newShipmentReference}
                          onChange={(e) => {
                            setNewShipmentReference(e.target.value);
                            if (shipmentReferenceError) {
                              setShipmentReferenceError('');
                            }
                          }}
                          placeholder="Enter shipment reference number"
                          variant="outlined"
                          error={!!shipmentReferenceError}
                          sx={{ 
                            backgroundColor: 'white',
                            '& .MuiInputBase-input': {
                              fontSize: '0.875rem',
                              padding: '8px'
                            }
                          }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleUpdateTrailerShipment}
                          disabled={!newShipmentReference.trim() || isUpdatingShipment}
                          startIcon={isUpdatingShipment ? <CircularProgress size={16} /> : <Check />}
                          sx={{ 
                            fontSize: '0.75rem',
                            minWidth: '80px',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {isUpdatingShipment ? 'Updating...' : 'Update'}
                        </Button>
                      </div>
                      {selectedUnifiedItem.shipmentNumber && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<Delete />}
                          onClick={() => setShowRemoveShipmentConfirm(true)}
                          disabled={!isRemoveShipmentButtonEnabled()}
                          sx={{
                            fontSize: '0.75rem',
                            opacity: !isRemoveShipmentButtonEnabled() ? 0.5 : 1,
                            mt: 1
                          }}
                        >
                          Remove Shipment Relationship
                        </Button>
                      )}
                    </div>
                  )}
                </div>

                {/* Location Information */}
                <div className="p-3 border rounded bg-purple-50">
                  <h3 className="font-semibold text-sm text-gray-700 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Location Information
                  </h3>
                  {selectedUnifiedItem.spotNumber && (
                    <p className="text-sm text-gray-600 mb-1">
                      Yard Spot: {selectedUnifiedItem.spotNumber}
                    </p>
                  )}
                  {selectedUnifiedItem.currentZone && (
                    <p className="text-sm text-gray-600 mb-1">
                      Zone: {selectedUnifiedItem.currentZone}
                    </p>
                  )}
                  {selectedUnifiedItem.spotStatus && (
                    <p className="text-sm text-gray-600 mb-1">
                      Spot Status: {selectedUnifiedItem.spotStatus}
                    </p>
                  )}
                  {selectedUnifiedItem.puid && (
                    <p className="text-sm text-gray-600">
                      PUID: {selectedUnifiedItem.puid}
                    </p>
                  )}
                  <LocationVerification
                    subject={{
                      type: selectedUnifiedItem.type,
                      lineage: selectedUnifiedItem.active_lineage,
                      occupied: !!selectedUnifiedItem.trailerNumber,
                      lastRtlsEmptyAt: selectedUnifiedItem.lastRtlsEmptyAt,
                      lastManualCheckAt: selectedUnifiedItem.lastManualCheckAt,
                    }}
                    place={{
                      facilityName: selectedUnifiedItem.facilityName || selectedFacilityLabel,
                      zoneName: selectedUnifiedItem.zoneName,
                      spotName: selectedUnifiedItem.spotNumber,
                    }}
                  />
                </div>
              </div>
              
              {/* Spot Notes (from configuration) */}
              {selectedUnifiedItem.notes && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                  <p className="text-xs font-medium text-amber-800 mb-1">Spot Notes</p>
                  <p className="text-sm text-amber-700">{selectedUnifiedItem.notes}</p>
                </div>
              )}

              <div className="mt-5 pt-3 border-t flex justify-end">
                <button
                  className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                  onClick={handleCloseUnifiedModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Success Modal */}
      {showSuccessModal && createdTask && (
        <Modal
          open={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setCreatedTask(null);
          }}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[500px] bg-white border p-6 max-h-[90vh] overflow-y-auto relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={() => {
                  setShowSuccessModal(false);
                  setCreatedTask(null);
                }}
              />
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="text-green-600 text-2xl" />
                </div>
                <h1 className="text-xl font-bold text-green-700 mb-2">Movement Task Created Successfully!</h1>
                <p className="text-gray-600">The trailer has been scheduled to move to the dock door.</p>
              </div>
              
              {/* Task Details */}
              <div className="bg-gray-50 border border-gray-200 rounded p-4 mb-4">
                <h2 className="font-semibold mb-3 text-gray-800">Task Details</h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Task ID:</span>
                    <span className="text-gray-800 font-mono text-sm">{createdTask.uuid}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Task Type:</span>
                    <span className="text-gray-800">{createdTask.task_type?.type || 'Movement Task'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-medium text-gray-600">Status:</span>
                    <span className="text-gray-800">{createdTask.task_status?.status || 'Created'}</span>
                  </div>
                  {createdTask.from_location && (
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">From:</span>
                      <span className="text-gray-800">{createdTask.from_location}</span>
                    </div>
                  )}
                  {createdTask.to_location && (
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">To:</span>
                      <span className="text-gray-800">{createdTask.to_location}</span>
                    </div>
                  )}
                  {createdTask.trailer?.trailer_number && (
                    <div className="flex justify-between">
                      <span className="font-medium text-gray-600">Trailer:</span>
                      <span className="text-gray-800">{createdTask.trailer.trailer_number}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t">
                <Button 
                  onClick={() => {
                    setShowSuccessModal(false);
                    setCreatedTask(null);
                  }}
                >
                  Close
                </Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={() => {
                    // Navigate to the task page
                    window.open(`/tasks/${createdTask.uuid}`, '_blank');
                  }}
                  startIcon={<ArrowForward />}
                >
                  View Task Details
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      
      {/* Move to Yard Spot Modal */}
      {showMoveToYardSpotModal && selectedStagingTrailer && (
        <Modal
          open={showMoveToYardSpotModal}
          onClose={handleCloseMoveToYardSpotModal}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[450px] bg-white border p-5 max-h-[90vh] overflow-y-auto relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleCloseMoveToYardSpotModal}
              />
              <h1 className="text-xl font-bold mb-4">Move Trailer to Yard Spot</h1>
              
              {/* Trailer details */}
              <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
                <div className="grid grid-cols-2 gap-2">
                  <div className="col-span-2">
                    <span className="font-semibold">Trailer:</span> {selectedStagingTrailer.trailer_number}
                  </div>
                  <div>
                    <span className="font-semibold">Driver:</span> {selectedStagingTrailer.driver_name}
                  </div>
                  <div>
                    <span className="font-semibold">Phone:</span> {selectedStagingTrailer.driver_phone_number}
                  </div>

                  {selectedStagingTrailer.carrier_name && (
                    <div className="col-span-2">
                      <span className="font-semibold">SCAC:</span> {selectedStagingTrailer.carrier_name}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Show error if any */}
              {assignYardSpotError && (
                <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
                  {assignYardSpotError}
                </div>
              )}
              
              {/* Shipment Reference Number field - only show if prefilled */}
              {selectedShipmentPuid && (
                <div className="mb-4">
                  <h2 className="font-semibold mb-2">Shipment Reference Number</h2>
                  <TextField
                    label="Enter Reference Number (Optional)"
                    variant="outlined"
                    fullWidth
                    value={selectedShipmentPuid}
                    onChange={(e) => setSelectedShipmentPuid(e.target.value)}
                    helperText={selectedShipmentPuid ? "Reference number is read-only when already assigned" : "Enter the shipment reference number if available (e.g. REF12345)"}
                    disabled={validatingShipment || assigningYardSpot || !!selectedShipmentPuid}
                  />
                </div>
              )}
              
              {/* Auto-complete checkbox */}
              <div className="mb-4">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={autoCompleteMoveToYardSpot}
                      onChange={(e) => setAutoCompleteMoveToYardSpot(e.target.checked)}
                      disabled={assigningYardSpot || validatingShipment}
                    />
                  }
                  label="Auto-complete task (task will be marked as completed immediately)"
                />
              </div>
              
              {/* Destination Type Selection */}
              <div className="mb-4">
                <h2 className="font-semibold mb-2">Select Destination Type</h2>
                <ToggleButtonGroup
                  value={selectedDestinationType}
                  exclusive
                  onChange={(_, value) => {
                    if (value) {
                      setSelectedDestinationType(value);
                      setSelectedYardSpotForMove('');
                      setSelectedYardSpotUuid('');
                      setSelectedFacilityZone('');
                      setAssignYardSpotError(null);
                    }
                  }}
                  fullWidth
                >
                  <ToggleButton value="yard_spot">
                    Yard Spot
                  </ToggleButton>
                  <ToggleButton value="facility_zone">
                    Facility Zone
                  </ToggleButton>
                </ToggleButtonGroup>
              </div>

              <DestinationFacilityFilter
                facilities={moveDestinations.facilities}
                value={moveDestinations.facilityFilter}
                onChange={value => {
                  moveDestinations.setFacilityFilter(value);
                  setSelectedYardSpotForMove('');
                  setSelectedYardSpotUuid('');
                  setSelectedZoneFilter(null);
                  setSpotFilter('');
                  setSelectedFacilityZone('');
                }}
                disabled={assigningYardSpot || validatingShipment}
              />

              {selectedDestinationType === 'yard_spot' && (
                <div>
                  {/* Zone filter dropdown */}
                  <div className="mb-4">
                    <h2 className="font-semibold mb-2">Filter by Zone (Optional)</h2>
                    <Autocomplete
                      options={moveDestinations.zones.map(zone => ({ 
                        label: zone.name, 
                        value: zone.id,
                        color: zone.color 
                      }))}
                      value={selectedZoneFilter ? moveDestinations.zones.find(z => z.id === selectedZoneFilter) ? { 
                        label: moveDestinations.zones.find(z => z.id === selectedZoneFilter)!.name, 
                        value: selectedZoneFilter,
                        color: moveDestinations.zones.find(z => z.id === selectedZoneFilter)!.color
                      } : null : null}
                      onChange={(_, newValue) => {
                        setSelectedZoneFilter(newValue?.value || null);
                        // Clear selected yard spot and spot filter when zone filter changes
                        setSelectedYardSpotForMove('');
                        setSelectedYardSpotUuid('');
                        setSpotFilter('');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Zone"
                          variant="outlined"
                          fullWidth
                          helperText="Filter yard spots by zone"
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div className="flex items-center">
                            <div 
                              className="w-3 h-3 rounded mr-2" 
                              style={{ backgroundColor: option.color }}
                            />
                            {option.label}
                          </div>
                        </li>
                      )}
                    />
                  </div>

                  {/* Available yard spots selection */}
                  <div className="mb-4">
                    <h2 className="font-semibold mb-2">Select Yard Spot</h2>
                  
                  <div className="space-y-3">
                    {/* Manual entry */}
                    {(() => {
                      // Get available spots for validation, filtered by zone if selected (only spots with valid displayNumber)
                      const availableSpots = moveDestinations.yardSpots
                        .filter(spot => {
                          const hasValidDisplayNumber = spot.displayNumber && spot.displayNumber.trim();
                          const isAvailable = spot.status === 'available' && !moveDestinations.isSpotLocked(spot.id);
                          const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter;
                          return hasValidDisplayNumber && isAvailable && matchesZone;
                        });
                      
                      // Helper function to get border color based on validation
                      const getBorderColor = () => {
                        const inputValue = selectedYardSpotForMove;
                        
                        // Empty input - blue
                        if (!inputValue.trim()) {
                          return '#2196F3'; // Blue
                        }
                        
                        // Check if input matches any available yard spot (case-insensitive)
                        const isValid = availableSpots.some(spot => 
                          spot.displayNumber.toLowerCase() === inputValue.toLowerCase()
                        );
                        
                        // Valid match - green, invalid - red
                        return isValid ? '#4CAF50' : '#F44336'; // Green or Red
                      };
                      
                      const borderColor = getBorderColor();
                      
                      return (
                        <TextField
                          label="Filter Yard Spots"
                          variant="outlined"
                          fullWidth
                          value={spotFilter}
                          onChange={(e) => {
                            const filterValue = e.target.value;
                            setSpotFilter(filterValue);
                            
                            // Auto-selection logic
                            if (filterValue.trim()) {
                              // Find spots that match the filter (only spots with valid displayNumber)
                              const matchingSpots = availableSpots.filter(spot => 
                                spot.displayNumber && 
                                spot.displayNumber.trim() && 
                                spot.displayNumber.toLowerCase().includes(filterValue.toLowerCase())
                              );
                              
                              // Check for exact match (only spots with valid displayNumber)
                              const exactMatch = matchingSpots.find(spot => 
                                spot.displayNumber && 
                                spot.displayNumber.trim() && 
                                spot.displayNumber.toLowerCase() === filterValue.toLowerCase()
                              );
                              
                              // Auto-select if there's exactly 1 result and it's an exact match
                              if (matchingSpots.length === 1 && exactMatch && exactMatch.displayNumber.trim()) {
                                setSelectedYardSpotForMove(exactMatch.displayNumber);
                                setSelectedYardSpotUuid(exactMatch.id);
                              } else {
                                // Clear selection if multiple results or no exact match
                                setSelectedYardSpotForMove('');
                                setSelectedYardSpotUuid('');
                              }
                            } else {
                              // Clear selection when filter is empty
                              setSelectedYardSpotForMove('');
                              setSelectedYardSpotUuid('');
                            }
                          }}
                          helperText="Type to filter spots below (e.g. A15)"
                          placeholder="Type spot name to filter..."
                        />
                      );
                    })()}
                    
                    {/* Quick selection from available spots */}
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Or select from available spots:</div>
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // Get available spots - show all available spots that are not locked, filtered by zone and spot filter if selected (only spots with valid displayNumber)
                        const availableSpots = moveDestinations.yardSpots
                          .filter(spot => {
                            const hasValidDisplayNumber = spot.displayNumber && spot.displayNumber.trim();
                            const isAvailable = spot.status === 'available' && !moveDestinations.isSpotLocked(spot.id);
                            const matchesZone = !selectedZoneFilter || spot.zoneId === selectedZoneFilter;
                            const matchesSpotFilter = !spotFilter.trim() || (hasValidDisplayNumber && spot.displayNumber.toLowerCase().includes(spotFilter.toLowerCase()));
                            return hasValidDisplayNumber && isAvailable && matchesZone && matchesSpotFilter;
                          });
                          
                        if (availableSpots.length === 0) {
                          return (
                            <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                              {spotFilter.trim() && selectedZoneFilter 
                                ? `No available yard spots found matching "${spotFilter}" in the selected zone. Try clearing the filters.`
                                : spotFilter.trim()
                                ? `No available yard spots found matching "${spotFilter}". Try clearing the filter.`
                                : selectedZoneFilter 
                                ? `No available yard spots found in the selected zone. Try clearing the zone filter.`
                                : `No available yard spots found.`
                              }
                            </div>
                          );
                        }
                        
                        return availableSpots.map(spot => {
                          // Find the zone name for this spot
                          const zone = moveDestinations.zones.find(z => z.id === spot.zoneId);
                          
                          return (
                            <button
                              key={spot.id}
                              className={`px-2 py-1 border rounded text-sm ${
                                selectedYardSpotUuid === spot.id 
                                  ? 'bg-blue-100 border-blue-500' 
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                              onClick={() => {
                                setSelectedYardSpotForMove(spot.displayNumber);
                                setSelectedYardSpotUuid(spot.id);
                                setSpotFilter(spot.displayNumber);
                              }}
                            >
                              <div className="flex flex-col">
                                <span className="font-medium">{spot.displayNumber}</span>
                                {zone && (
                                  <span className="text-xs text-gray-500">{zone.name}</span>
                                )}
                              </div>
                            </button>
                          );
                        });
                      })()}
                    </div>
                  </div>
                </div>
              </div>
                </div>
              )}

              {selectedDestinationType === 'facility_zone' && (
                /* Facility zone selection */
                <div className="mb-4">
                  <h2 className="font-semibold mb-2">Select Facility Zone</h2>
                  
                  <div className="space-y-3">
                    {/* Quick selection from available facility zones */}
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Select from available facility zones:</div>
                      <div className="flex flex-wrap gap-2">
                        {(() => {
                          // Get available facility zones
                          const availableFacilityZones = moveDestinations.facilityZones;
                          
                          if (availableFacilityZones.length === 0) {
                            return (
                              <div className="text-amber-600 p-2 border border-amber-200 rounded text-sm bg-amber-50 w-full">
                                No facility zones found.
                              </div>
                            );
                          }
                          
                          return availableFacilityZones.map((zone: any) => {
                            return (
                              <button
                                key={zone.facility_zone_uuid}
                                className={`px-3 py-2 border rounded text-sm ${
                                  selectedFacilityZone === zone.facility_zone_uuid 
                                    ? 'bg-blue-100 border-blue-500' 
                                    : 'bg-gray-50 hover:bg-gray-100'
                                }`}
                                onClick={() => setSelectedFacilityZone(zone.facility_zone_uuid)}
                              >
                                <div className="flex flex-col">
                                  <span className="font-medium">{zone.facility_zone_name}</span>
                                  {zone.zone_type && (
                                    <span className="text-xs text-gray-500">{zone.zone_type}</span>
                                  )}
                                </div>
                              </button>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex justify-between pt-3 border-t">
                <Button onClick={handleCloseMoveToYardSpotModal}>Cancel</Button>
                <Button 
                  variant="contained" 
                  color="primary"
                  onClick={handleAssignYardSpot}
                  disabled={
                    (selectedDestinationType === 'yard_spot' && !selectedYardSpotForMove.trim()) ||
                    (selectedDestinationType === 'facility_zone' && !selectedFacilityZone.trim()) ||
                    assigningYardSpot || validatingShipment
                  }
                >
                  {validatingShipment ? (
                    <>
                      <CircularProgress size={24} className="mr-2" />
                      Validating...
                    </>
                  ) : assigningYardSpot ? (
                    <CircularProgress size={24} />
                  ) : (
                    'Move Trailer'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Zone Trailer Details Modal */}
      {showZoneTrailerDetailsModal && selectedZoneTrailer && (
        <Modal
          open={showZoneTrailerDetailsModal}
          onClose={handleCloseZoneTrailerDetailsModal}
        >
          <div className="flex justify-center items-center w-full h-full">
            <div className="w-[500px] bg-white border p-5 max-h-[80vh] overflow-y-auto relative">
              <Close
                className="absolute top-4 right-4 cursor-pointer hover:text-slate-400"
                onClick={handleCloseZoneTrailerDetailsModal}
              />
              <h1 className="text-xl font-bold mb-5">Trailer {selectedZoneTrailer.trailer_number} Details</h1>
              
              {/* Trailer Information */}
              <div className="mb-4">
                <h2 className="font-bold mb-2">Trailer Information</h2>
                <div className="p-3 border rounded bg-gray-50">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-semibold">Trailer:</span> {selectedZoneTrailer.trailer_number}
                    </div>
                    {selectedZoneTrailer.carrier_name && (
                      <div>
                        <span className="font-semibold">SCAC:</span> {selectedZoneTrailer.carrier_name}
                      </div>
                    )}
                    {selectedZoneTrailer.current_status && (
                      <div>
                        <span className="font-semibold">Status:</span> {selectedZoneTrailer.current_status}
                      </div>
                    )}
                    {selectedZoneTrailer.direction && (
                      <div>
                        <span className="font-semibold">Direction:</span> {formatDirection(selectedZoneTrailer.direction)}
                      </div>
                    )}
                    {selectedZoneTrailer.shipment_number && (
                      <div className="col-span-2">
                        <span className="font-semibold">Shipment:</span> {selectedZoneTrailer.shipment_number}
                      </div>
                    )}
                    {lineageCheckIn(selectedZoneTrailer.active_lineage) && (
                      <div className="col-span-2">
                        <span className="font-semibold">Trailer Check-In Date:</span> {new Date(lineageCheckIn(selectedZoneTrailer.active_lineage)!).toLocaleString()}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mb-4">
                <div className="flex gap-2">
                  <button 
                    className="bg-green-500 hover:bg-green-600 text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1"
                    onClick={() => {
                      handleCloseZoneTrailerDetailsModal();
                      handleMoveZoneTrailerToYardSpot(selectedZoneTrailer);
                    }}
                  >
                    <ArrowForward fontSize="small" className="mr-2" />
                    <span>Move to Yard Spot</span>
                  </button>
                  <button 
                    className={`${
                      selectedZoneTrailer && isTrailerOOS(selectedZoneTrailer)
                        ? 'bg-gray-400 cursor-not-allowed' 
                        : 'bg-blue-500 hover:bg-blue-600'
                    } text-white text-sm py-2 px-4 rounded flex items-center font-medium flex-1`}
                    disabled={selectedZoneTrailer && isTrailerOOS(selectedZoneTrailer)}
                    onClick={() => {
                      handleCloseZoneTrailerDetailsModal();
                      handleMoveZoneTrailerToDock(selectedZoneTrailer);
                    }}
                    title={
                      selectedZoneTrailer && isTrailerOOS(selectedZoneTrailer)
                        ? 'Trailer is out of service'
                        : 'Move to Dock'
                    }
                  >
                    <ArrowForward fontSize="small" className="mr-2" />
                    <span>Move to Dock</span>
                  </button>
                </div>
              </div>

              {/* Trailer Notes Section */}
              {selectedZoneTrailer.trailer_uuid && (
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="font-bold">Trailer Notes</h2>
                    {!isEditingTrailerNotes && (
                      <button
                        className="text-sky-600 text-sm hover:underline flex items-center"
                        onClick={() => setIsEditingTrailerNotes(true)}
                      >
                        <Edit fontSize="small" className="mr-1" />
                        {selectedZoneTrailer.active_lineage?.notes ? 'Edit Trailer Notes' : 'Add Trailer Notes'}
                      </button>
                    )}
                  </div>
                  
                  {isEditingTrailerNotes ? (
                    <div>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        value={trailerNotesValue}
                        onChange={(e) => setTrailerNotesValue(e.target.value)}
                        placeholder="Enter notes about this trailer..."
                        variant="outlined"
                        disabled={savingTrailerNotes}
                      />
                      <div className="flex justify-end gap-2 mt-2">
                        <Button
                          size="small"
                          onClick={() => {
                            setTrailerNotesValue(selectedZoneTrailer.active_lineage?.notes || '');
                            setIsEditingTrailerNotes(false);
                          }}
                          disabled={savingTrailerNotes}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSaveTrailerNotes}
                          disabled={savingTrailerNotes}
                          startIcon={savingTrailerNotes ? <CircularProgress size={16} /> : undefined}
                        >
                          {savingTrailerNotes ? 'Saving...' : 'Save Trailer Notes'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-3 border rounded bg-green-50">
                      {selectedZoneTrailer.active_lineage?.notes ? (
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{selectedZoneTrailer.active_lineage.notes}</p>
                      ) : (
                        <p className="text-sm text-gray-500 italic">No trailer notes added</p>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <div className="mt-5 pt-3 border-t flex justify-end">
                <button
                  className="bg-sky-600 hover:bg-sky-500 text-white p-2 rounded"
                  onClick={handleCloseZoneTrailerDetailsModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Task Details Modal */}
      <TaskDetailsModal
        open={showTaskDetailsModal}
        onClose={() => {
          setShowTaskDetailsModal(false);
          setSelectedTaskForDetails(null);
        }}
        task={selectedTaskForDetails}
        onCancelTask={handleCancelTask}
        onCompleteTask={handleCompleteTask}
        onCompleteMovementTask={handleCompleteMovementTask}
      />

      {/* Remove Shipment Confirmation Dialog */}
      <Dialog
        open={showRemoveShipmentConfirm}
        onClose={() => setShowRemoveShipmentConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete />
            Remove Shipment Relationship
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove the shipment relationship from trailer <strong>{selectedSpot?.trailerNumber || selectedUnifiedItem?.trailerNumber}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current shipment: <strong>{selectedSpot?.shipmentNumber || selectedUnifiedItem?.shipmentNumber}</strong>
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'medium' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setShowRemoveShipmentConfirm(false)}
            disabled={isRemovingShipment}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRemoveShipmentRelationship}
            variant="contained"
            color="error"
            disabled={isRemovingShipment}
            startIcon={isRemovingShipment ? <CircularProgress size={16} /> : <Delete />}
          >
            {isRemovingShipment ? 'Removing...' : 'Remove Relationship'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
});

export default YardSpotManagement; 